import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import PriceImport from "./PriceImport";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";

// Breaker/panel brand groups (key → label) for the manual override select.
// Mirrors api/_brand.js BRAND_LABELS. "" = auto-detect.
const BRAND_CHOICES = [
  ["SIEMENS", "Siemens"], ["SQUARE_D_QO", "Square D (QO)"], ["HOMELINE", "Square D Homeline"],
  ["EATON_BR", "Eaton (BR)"], ["EATON_CH", "Eaton (CH)"], ["GE", "GE"], ["LEVITON", "Leviton"],
  ["FEDERAL_PIONEER", "Federal Pioneer"],
];

function fmtCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(num);
}

function fmtDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

const OVERCHARGE_THRESHOLD = 0.10;

// How many product cards to paint at once, and how many more each
// "Show more" click adds.
const PAGE_SIZE = 100;

// Map every canonical_key prefix (built by api/_canonical.js) to a friendly
// category label. The prefix carries hierarchy for free — WIRE|NMD90|14|2
// is wire-cable-NMD90-14awg. We use the first segment as the top category.
const CATEGORY_LABELS = {
  WIRE: "Wire & Cable",
  CONDUIT: "Conduit",
  BOX: "Boxes & Covers",
  BREAKER: "Breakers",
  DEVICE: "Devices",
  CONNECTOR: "Connectors & Fittings",
  LIGHT: "Lighting",
  DATA: "Low Voltage / Data",
  TOOL: "Tools",
  FASTENER: "Fasteners",
  SOLAR: "Solar",
  GENERATOR: "Generators",
  MINISPLIT: "Mini Splits",
  RAW: "Other / Uncategorized",
};

// Manual categories (master_products.category) that get their own pill code
// instead of collapsing into "Manual category". Lets the user filter on a
// useful bucket even when canonical_key hasn't been computed yet — including
// the keyword-classified labels written by the price-list import
// (api/_categoryClassifier.js), so the "misc" tail lands in real pills.
const MANUAL_CATEGORY_TO_CODE = {
  "Generators": "GENERATOR",
  "Mini Splits": "MINISPLIT",
  "Wire & Cable": "WIRE",
  "Conduit": "CONDUIT",
  "Boxes & Covers": "BOX",
  "Breakers": "BREAKER",
  "Devices": "DEVICE",
  "Connectors & Fittings": "CONNECTOR",
  "Lighting": "LIGHT",
  "Low Voltage / Data": "DATA",
  "Tools": "TOOL",
  "Fasteners": "FASTENER",
  "Solar": "SOLAR",
};

// Description-based lighting detector. Catches products whose canonical_key
// hasn't been recomputed yet (still RAW) or whose category is set manually.
// Used by the Lights pill so the user sees lighting items even before the
// admin "Recanonicalize products" sweep has run.
const LIGHTING_KEYWORDS_RX = /\b(?:pot[\s-]?light|potlight|down[\s-]?light|downlight|wafer|recessed|luminaire|fixture|sconce|pendant|chandelier|troffer|fluorescent|led\s*tube|tube\s*light|strip\s*light|tape\s*light|rope\s*light|exit\s*sign|emergency\s*light|track\s*light|puck\s*light|under[\s-]?cabinet|vanity\s*light|flush\s*mount|bulb|a19|a21|br30|br40|mr16|par\d{2})\b/i;

function looksLikeLighting(row) {
  const key = String(row?.canonical_key || "").toUpperCase();
  if (key.startsWith("LIGHT|")) return true;
  const haystack = `${row?.description || ""} ${row?.sku || ""} ${row?.category || ""} ${(row?.tradeNames || []).join(" ")}`;
  return LIGHTING_KEYWORDS_RX.test(haystack);
}

// Cover / face-plate detector. These descriptions abbreviate "raised" as RSD
// (e.g. 3/8"RSD = 3/8" raised cover), which the canonicalizer historically
// mis-read as the solar "Rapid Shutdown" term and bucketed under Solar. This
// surfaces them under Boxes & Covers instead — and catches plates still
// carrying a RAW key or a manual "Covers & Plates" category.
const COVER_PLATE_RX = /\b(?:cover|face\s*plate|faceplate|wall\s*plate|wallplate|mud\s*ring|mudring|blank\s*plate)\b/i;
// Strong solar signals — never reclassify a genuine PV product as a cover just
// because its description happens to mention a "cover".
const SOLAR_HARD_RX = /\b(?:photovoltaic|pv\s*module|solar\s*module|solar\s*panel|micro\s*-?\s*inverter|optimizer|mc4|mlpe|rapid\s*shutdown)\b/i;

function looksLikeCoverPlate(row) {
  const haystack = `${row?.description || ""} ${row?.sku || ""} ${row?.category || ""}`;
  if (SOLAR_HARD_RX.test(haystack)) return false;
  if (COVER_PLATE_RX.test(haystack)) return true;
  return String(row?.category || "").trim().toLowerCase() === "covers & plates";
}

function getProductCategory(row) {
  const key = String(row?.canonical_key || "").toUpperCase();
  const prefix = key.includes("|") ? key.split("|")[0] : "";
  // Cover plates first: "RSD" here means "raised", not solar Rapid Shutdown,
  // so a stale SOLAR key (or an uncomputed RAW one) shouldn't strand them in
  // the wrong pill. Only override the generic/mistaken buckets — a specific
  // family prefix (WIRE, BREAKER, …) is left alone.
  if ((prefix === "" || prefix === "RAW" || prefix === "SOLAR") && looksLikeCoverPlate(row)) {
    return "BOX";
  }
  // A specific family prefix from the canonicalizer wins. (RAW is excluded so
  // a keyword-classified category column below can promote a "misc" item.)
  if (prefix && prefix !== "RAW" && CATEGORY_LABELS[prefix]) return prefix;
  // Fall back to the `category` column — covers manual categories AND the
  // keyword-classified labels the price-list import writes for "misc" items.
  const cat = String(row?.category || "").trim();
  if (MANUAL_CATEGORY_TO_CODE[cat]) return MANUAL_CATEGORY_TO_CODE[cat];
  if (prefix === "RAW") return "RAW";
  if (cat && cat !== "Imported Invoice") return "MANUAL";
  return "RAW";
}

function getProductCategoryLabel(row) {
  const code = getProductCategory(row);
  if (code === "MANUAL") return String(row?.category || "Other");
  return CATEGORY_LABELS[code] || "Other";
}

function getOverchargePct(row) {
  const latest = Number(row?.latestPrice);
  const best = Number(row?.bestPrice);
  if (!Number.isFinite(latest) || !Number.isFinite(best) || best <= 0) return null;
  if ((row?.supplierCount || 0) < 2) return null;
  if (!row?.latestSupplier || !row?.bestSupplier) return null;
  if (row.latestSupplier === row.bestSupplier) return null;
  const pct = (latest - best) / best;
  return pct >= OVERCHARGE_THRESHOLD ? pct : null;
}

function normalize(value) {
  return String(value || "").toLowerCase();
}

function normalizeCompact(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function tokenize(value) {
  return normalize(value).split(/[^a-z0-9/#.-]+/).filter(Boolean);
}

const SEARCH_EQUIVALENTS = new Map([
  ["lumex", ["loomex"]],
  ["loomex", ["lumex"]],
  ["meter", ["mtr"]],
  ["mtr", ["meter"]],
  ["meterbase", ["meter base", "mtr base", "meter socket"]],
  ["meterbase", ["meter base", "mtr base", "meter socket"]],
  ["wp", ["weatherproof"]],
  ["weatherproof", ["wp"]],
  // Lighting — suppliers call the same physical thing many ways. Mapping
  // these so a search for "pot light" hits products labeled "downlight",
  // "wafer", "recessed", "luminaire", etc.
  // Keep in sync with api/_searchVocab.js — "gimbal"/"disk light"/"waffer"
  // (City Electric's spelling)/"slim" are how the pot lights Browning actually
  // buys are catalogued; without them the browse tab fails the exact search
  // the server-side fix was built for.
  ["pot", ["potlight", "downlight", "wafer", "waffer", "recessed", "can", "canless", "gimbal", "disk light", "disc light", "slim", "lp downlight"]],
  ["potlight", ["pot light", "downlight", "wafer", "waffer", "recessed", "can light", "canless", "gimbal", "disk light", "slim"]],
  ["potlights", ["pot lights", "downlights", "wafers", "recessed lights", "gimbal", "disk light"]],
  ["downlight", ["pot light", "potlight", "wafer", "waffer", "recessed", "can light", "gimbal", "disk light"]],
  ["wafer", ["waffer", "pot light", "potlight", "downlight", "recessed", "slim"]],
  ["recessed", ["pot light", "potlight", "downlight", "wafer", "waffer", "can light", "gimbal", "canless"]],
  ["can", ["pot light", "potlight", "downlight", "wafer", "recessed"]],
  ["gimbal", ["floating gimbal", "downlight", "pot light", "adjustable"]],
  ["disk", ["disc", "disk light", "downlight", "pot light"]],
  ["light", ["lights", "lighting", "luminaire", "fixture", "lamp", "bulb"]],
  ["lights", ["light", "lighting", "luminaire", "fixture", "lamp"]],
  ["fixture", ["light", "lights", "luminaire", "fitting"]],
  ["luminaire", ["fixture", "light", "lights"]],
  ["bulb", ["lamp", "led bulb"]],
  ["lamp", ["bulb", "lamp"]],
  ["black", ["blk"]],
  ["white", ["wht"]],
  ["ivory", ["ivy"]],
]);

const SEARCH_PHRASES = [
  { pattern: /spa\s*pack/, concepts: [["spa", "hot tub"], ["panel", "disconnect", "load center", "load ctr"]] },
  { pattern: /car charger plug|ev plug|charger plug/, concepts: [["ev", "car charger"], ["receptacle", "plug", "14-50", "50a"]] },
  { pattern: /dryer plug/, concepts: [["dryer"], ["receptacle", "plug", "30a"]] },
  { pattern: /range plug/, concepts: [["range"], ["receptacle", "plug", "50a"]] },
  { pattern: /meter\s*base|meterbase/, concepts: [["meter", "mtr"], ["base", "socket", "can"]] },
  { pattern: /wp\s*panel/, concepts: [["wp", "weatherproof", "outdoor"], ["panel", "nema 3r"]] },
  { pattern: /\bdisconnect\b/, concepts: [["disconnect", "switch"], ["ac", "safety"]] },
  // Pot lights / recessed lighting — flatten "4 pot lights", "4in downlight",
  // "4\" wafer" etc. into one shared concept set so they all return the same
  // results regardless of which term the supplier used.
  { pattern: /pot\s*light|potlight|down\s*light|downlight|wafer|recessed/, concepts: [["pot", "potlight", "pot light", "downlight", "down light", "wafer", "recessed", "can light", "canless", "luminaire", "trim"]] },
];

const SEARCH_INTENTS = [
  {
    pattern: /spa\s*pack|hot tub panel/,
    include: ["spa", "hot tub", "load ctr", "load center", "disconnect", "gfi"],
    prefer: ["spa load ctr", "spa panel", "hot tub panel"],
    exclude: ["workspace"],
  },
  {
    pattern: /car charger plug|ev plug|charger plug/,
    include: ["ev", "charger", "14-50", "50a", "receptacle"],
    prefer: ["car charger plug", "ev receptacle", "14-50 receptacle", "tesla plug"],
  },
  {
    pattern: /wp\s*panel/,
    include: ["panel", "load center", "loadcentre", "weatherproof", "outdoor", "nema 3r"],
    prefer: ["wp panel", "weatherproof panel", "outdoor panel", "nema 3r panel"],
    exclude: ["cover", "plate"],
  },
  {
    pattern: /\bdisconnect\b/,
    include: ["disconnect", "ac disconnect", "safety switch", "switch"],
    prefer: ["disconnect", "ac disconnect", "safety switch"],
    exclude: ["box", "cover", "plate"],
  },
  {
    pattern: /range plug/,
    include: ["range", "50a", "receptacle", "plug"],
    prefer: ["range plug", "range receptacle"],
  },
];

// Mirror of api/_searchVocab.js classifyToken — tokens that LOOK like part
// numbers but are trade vocabulary must stay concept tokens.
const TRADE_TOKEN_RE = new RegExp("^(?:" + [
  "nmd90", "nmd", "nmwu", "teck90", "teck", "tck", "acwu90", "acwu", "ac90", "bx",
  "rw90", "rwu90", "t90", "thhn", "emt", "pvc", "led", "cct", "gfci", "afci",
  "evse", "nema", "awg", "kcmil", "mcm", "a19",
  "\\d{1,4}(?:a|v|w|va|kw|kva|hp|amp|amps|volt|volts|watt|watts)",
  "\\d{1,2}awg", "\\d{3,4}(?:mcm|kcmil|kcm)", "\\d{1,2}p", "[1-4]p\\d{2,3}a?",
  // "3c6"/"2c14" is conductor-count cable notation, not a part number.
  "\\d(?:way|gang|pole|phase|c)", "[2-5]c#?\\d{1,2}", "\\d{1,2}(?:in|inch|ft|mm|m)", "par\\d{2}", "\\d{1,2}cct",
].join("|") + ")$", "i");

function classifySearchToken(token) {
  const compact = normalizeCompact(token);
  if (compact.length < 3) return "word";
  if (TRADE_TOKEN_RE.test(compact)) return "word";
  if (/\d/.test(compact) && /[a-z]/.test(compact)) return "identifier";
  if (/^\d{4,}$/.test(compact)) return "identifier";
  return "word";
}

function longestCommonSubstringLen(a = "", b = "") {
  if (!a || !b) return 0;
  let prev = new Array(b.length + 1).fill(0);
  let best = 0;
  for (let i = 1; i <= a.length; i += 1) {
    const cur = new Array(b.length + 1).fill(0);
    for (let j = 1; j <= b.length; j += 1) {
      if (a[i - 1] === b[j - 1]) { cur[j] = prev[j - 1] + 1; if (cur[j] > best) best = cur[j]; }
    }
    prev = cur;
  }
  return best;
}

// Digit-pair needles ("14/2", "2 0") match at number boundaries only, with
// range triples ("14 2 0" — a lug's normalized "14-2/0" span) blanked first;
// short pure-letter needles match at word starts (the cooLED/CANADIAN rule).
function needleHits(haystack, tokenSet, needle) {
  const pair = needle.match(/^(\d{1,2})[\s/-](\d{1,2})$/);
  if (pair) {
    const hay = haystack.replace(/(^|[^0-9a-z])\d{1,2}\s*[-\s/]\s*[1-4]\s*[-\s/]\s*0(?![0-9])/g, "$1 ");
    return new RegExp(`(^|[^0-9])${pair[1]}(?:\\s*[/-]\\s*|\\s+)${pair[2]}([^0-9]|$)`).test(hay);
  }
  if (needle.includes(" ")) return haystack.includes(needle);
  if (needle.length < 4 && /^[a-z]+$/.test(needle)) return tokenSet.has(needle);
  return haystack.includes(needle);
}

function buildSearchConcepts(value) {
  const normalized = normalize(value).trim();
  if (!normalized) return { concepts: [], identifiers: [], required: [] };

  const concepts = [];
  const identifiers = [];
  const required = [];
  const claimed = new Set();

  // Wire gauges are identity — "14/2"/"14-2" become a REQUIRED concept with
  // the supplier variant set, instead of being shredded into droppable digits.
  for (const m of normalized.matchAll(/(^|[^0-9a-z])(\d{1,2})\s*[/-]\s*([2-4])(?![0-9])(?!\s*\/)(?!\s*(?:"|''|inch|in\b))/g)) {
    const g = m[2];
    const c = m[3];
    if (![1, 2, 3, 4, 6, 8, 10, 12, 14].includes(Number(g))) continue;
    required.push([`${g}/${c}`, `${g}-${c}`, `${g} ${c}`, `${c}c${g}`, `${c}c ${g}`]);
    claimed.add(g); claimed.add(c); claimed.add(`${g}/${c}`); claimed.add(`${g}-${c}`);
  }
  for (const m of normalized.matchAll(/(^|[^0-9a-z])([1-4])\s*[/-]\s*0(?![0-9])/g)) {
    const x = m[2];
    required.push([`${x}/0`, `${x}-0`, `${x} 0`]);
    claimed.add(x); claimed.add("0"); claimed.add(`${x}/0`); claimed.add(`${x}-0`);
  }

  // Part-number tokens are kept INTACT and REQUIRED — matching happens in
  // separator-free compact space with a minimum contiguous run (Gescan-style),
  // so "DVRF-6L-WH" or "QO 120" can't shred into 50-result fragments.
  const rawTokens = normalized.split(/\s+/).filter(Boolean);
  for (let i = 0; i < rawTokens.length; i += 1) {
    const t = rawTokens[i];
    if (claimed.has(t)) continue;
    if (classifySearchToken(t) === "identifier") {
      identifiers.push(normalizeCompact(t));
      claimed.add(t);
      continue;
    }
    const next = rawTokens[i + 1];
    if (next && /^[a-z]{1,3}$/.test(normalizeCompact(t)) && !SEARCH_EQUIVALENTS.get(t) && !TRADE_TOKEN_RE.test(normalizeCompact(t))
        && /^\d{2,4}$/.test(normalizeCompact(next)) && !claimed.has(next)) {
      identifiers.push(`${normalizeCompact(t)}${normalizeCompact(next)}`);
      claimed.add(t); claimed.add(next);
      i += 1;
    }
  }

  // Phrases claim their own words so they don't also become weak single-token
  // concepts that dilute (or, worse, hard-gate) the score — "pot light" must
  // be ONE concept the FLOATING GIMBAL row can satisfy.
  for (const phrase of SEARCH_PHRASES) {
    if (phrase.pattern.test(normalized)) {
      concepts.push(...phrase.concepts);
      for (const w of (normalized.match(phrase.pattern)?.[0] || "").split(/\s+/)) claimed.add(w);
    }
  }

  const tokens = normalized.split(/[^a-z0-9/#.-]+/).filter(Boolean);
  for (const token of tokens) {
    if (claimed.has(token) || normalizeCompact(token).length < 2) continue;
    const variants = new Set([token, normalizeCompact(token)]);
    const equivalents = SEARCH_EQUIVALENTS.get(token) || [];
    for (const equivalent of equivalents) {
      variants.add(normalize(equivalent));
      variants.add(normalizeCompact(equivalent));
    }
    concepts.push(Array.from(variants).filter(Boolean));
  }

  return { concepts, identifiers, required };
}

function getRowSearchScore(row, query, termNorm) {
  const parts = [
    row.sku,
    row.description,
    row.category,
    row.unit,
    row.mfg_part_number,
    ...(row.tradeNames || []),
    row.latestSupplier,
    row.bestSupplier,
    ...((row.supplierRows || []).map((supplierRow) => `${supplierRow.supplier_name} ${supplierRow.supplier_description || ""} ${supplierRow.supplier_sku || ""} ${supplierRow.supplier_cat_code || ""} ${supplierRow.mfg_part_number || ""}`)),
  ];
  const normalizedParts = parts.map(normalize);
  const haystack = normalizedParts.join(" ");
  const compactHaystack = parts.map(normalizeCompact).join(" ");
  const tokenSet = new Set(parts.flatMap((part) => tokenize(part)));

  // Identifiers (part numbers) are REQUIRED: exact/prefix sku beats a
  // contiguous hit elsewhere beats a >=60% longest-common-run — anything
  // less drops the row entirely.
  let idRank = 0;
  if (query.identifiers.length) {
    // Typing a part number off a shelf label or an old PO must land the row,
    // so the clean order codes rank at the same tier as the internal sku.
    const skuCompacts = [
      normalizeCompact(row.sku),
      normalizeCompact(row.mfg_part_number),
      ...((row.supplierRows || []).flatMap((r) => [
        normalizeCompact(r.supplier_sku),
        normalizeCompact(r.supplier_cat_code),
        normalizeCompact(r.mfg_part_number),
      ])),
    ].filter(Boolean);
    for (const idc of query.identifiers) {
      const minRun = /^\d+$/.test(idc) ? Math.min(4, idc.length) : 3;
      let tier = 0;
      let cov = 0;
      if (skuCompacts.some((s) => s === idc)) { tier = 3; cov = 1; }
      else if (skuCompacts.some((s) => s.startsWith(idc))) { tier = 2.5; cov = 1; }
      else if (compactHaystack.includes(idc)) { tier = 2; cov = 1; }
      else {
        const lcs = longestCommonSubstringLen(compactHaystack, idc);
        if (lcs >= minRun && lcs / idc.length >= 0.6) { tier = 1; cov = lcs / idc.length; }
      }
      if (!tier) return -1;
      idRank += tier * 1000 + cov * 100;
    }
  }
  // Wire gauges are required too — a 14/2 search must not surface 10/3 rows.
  for (const rc of query.required) {
    if (!rc.some((needle) => needleHits(haystack, tokenSet, needle))) return -1;
  }
  // Word concepts are SCORED, not hard-required (matching the server): the
  // FLOATING GIMBAL row contains neither "pot" nor "light" and must still
  // rank on a "pot light" search. At least one concept must hit.
  let score = idRank + (query.required.length ? 2 : 0);
  let conceptHits = 0;
  for (const concept of query.concepts) {
    let conceptMatched = false;
    for (const needle of concept) {
      if (needleHits(haystack, tokenSet, needle) || (normalizeCompact(needle).length >= 4 && compactHaystack.includes(normalizeCompact(needle)))) {
        conceptMatched = true;
        score += needle.includes(" ") ? 3 : 1;
      }
    }
    if (conceptMatched) conceptHits += 1;
  }
  if (query.concepts.length && !conceptHits && !query.identifiers.length && !query.required.length) return -1;
  // Intents describe what the TECH TYPES — they gate on the query, then
  // measure include/prefer/exclude on the row. (Testing the row text instead
  // meant /range plug/ never fired while every row containing "disconnect"
  // got a permanent unconditional boost.)
  for (const intent of SEARCH_INTENTS) {
    if (!termNorm || !intent.pattern.test(termNorm)) continue;
    const includeHits = (intent.include || []).filter((needle) => haystack.includes(normalize(needle)) || compactHaystack.includes(normalizeCompact(needle))).length;
    const preferHits = (intent.prefer || []).filter((needle) => haystack.includes(normalize(needle)) || compactHaystack.includes(normalizeCompact(needle))).length;
    const excludeHits = (intent.exclude || []).filter((needle) => haystack.includes(normalize(needle)) || compactHaystack.includes(normalizeCompact(needle))).length;
    score += includeHits * 2 + preferHits * 5 - excludeHits * 4;
  }
  const meterBonus = (row.tradeNames || []).some((name) => normalize(name).includes("meter base") || normalize(name).includes("meter socket")) ? 3 : 0;
  return score + meterBonus;
}

export default function PriceBook() {
  // Sync, Negotiation Report, and the Admin maintenance dropdown are
  // owner/admin-only — crew don't need (and shouldn't see) those tools.
  // Sync is also redundant in normal use because the daily cron at
  // /api/pricebook?action=syncInvoicePrices already runs it automatically.
  const { isAdmin } = usePermissions();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [reviewItems, setReviewItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showOverchargedOnly, setShowOverchargedOnly] = useState(false);
  const [activeTab, setActiveTab] = useState("browse");
  const [syncing, setSyncing] = useState(false);
  const [resolvingKey, setResolvingKey] = useState("");
  const [manualPrices, setManualPrices] = useState({});
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [syncReport, setSyncReport] = useState(null);
  const [error, setError] = useState("");
  const [openMenuId, setOpenMenuId] = useState("");
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeSearch, setMergeSearch] = useState("");
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [splitBusy, setSplitBusy] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  // (Manual cleanup button removed — runs automatically at the end of every
  // syncInvoicePrices now. Standalone /api action still works for ops use.)
  const [invoiceLoadingKey, setInvoiceLoadingKey] = useState("");
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [backfillReport, setBackfillReport] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [csvBackfillReport, setCsvBackfillReport] = useState(null);
  const [csvBackfillBusy, setCsvBackfillBusy] = useState(false);
  const [csvSourceModal, setCsvSourceModal] = useState(null);
  const [purgeReport, setPurgeReport] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [recanonReport, setRecanonReport] = useState(null);
  const [recanonBusy, setRecanonBusy] = useState(false);
  const [packReport, setPackReport] = useState(null);
  const [packBusy, setPackBusy] = useState(false);
  const [orphanReport, setOrphanReport] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [brandFilter, setBrandFilter] = useState("ALL"); // breaker/panel brand
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // The book is ~3,400 products. Painting every card is ~90k DOM nodes, which
  // blocked the main thread for 3-4s per keystroke — the typed character and
  // the filtered list both landed seconds late, so search looked broken. Cap
  // what we paint and let the user pull more in.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Keep the input responsive: React paints the typed character first, then
  // re-filters against the deferred value.
  const deferredSearch = useDeferredValue(search);

  const loadReviewQueue = useCallback(async () => {
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "getReviewQueue" }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Could not load review queue");
      setReviewItems(data.items || []);
    } catch (err) {
      setError(err.message || "Could not load review queue");
    }
  }, []);

  const loadPriceBook = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "getCatalog" }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Could not load price book");
      setProducts(data.products || []);
      setSuppliers(data.suppliers || []);
    } catch (err) {
      setError(err.message || "Could not load price book");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPriceBook();
  }, [loadPriceBook]);

  useEffect(() => {
    loadReviewQueue();
  }, [loadReviewQueue]);

  const filteredRows = useMemo(() => {
    const query = buildSearchConcepts(deferredSearch);
    const termNorm = normalize(deferredSearch).trim();
    let base = products;
    if (query.concepts.length || query.identifiers.length || query.required.length) {
      base = products
        .map((row) => ({ row, score: getRowSearchScore(row, query, termNorm) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .map(({ row }) => row);
    }
    if (categoryFilter === "LIGHT") {
      base = base.filter(looksLikeLighting);
    } else if (categoryFilter && categoryFilter !== "ALL") {
      base = base.filter((row) => getProductCategory(row) === categoryFilter);
    }
    if (dateFrom) {
      const fromMs = new Date(dateFrom).getTime();
      if (Number.isFinite(fromMs)) {
        base = base.filter((row) => {
          const d = new Date(row.latestDate || 0).getTime();
          return Number.isFinite(d) && d >= fromMs;
        });
      }
    }
    if (dateTo) {
      const toMs = new Date(dateTo).getTime() + 24 * 60 * 60 * 1000 - 1; // inclusive end-of-day
      if (Number.isFinite(toMs)) {
        base = base.filter((row) => {
          const d = new Date(row.latestDate || 0).getTime();
          return Number.isFinite(d) && d <= toMs;
        });
      }
    }
    if (showOverchargedOnly) {
      base = base.filter((row) => getOverchargePct(row) !== null);
    }
    if (brandFilter && brandFilter !== "ALL") {
      base = brandFilter === "__NONE__"
        ? base.filter((row) => !row.brand_label)
        : base.filter((row) => row.brand_label === brandFilter);
    }
    return base;
  }, [products, deferredSearch, showOverchargedOnly, categoryFilter, brandFilter, dateFrom, dateTo]);

  // Any change to the query or filters starts the list over at the top.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [deferredSearch, showOverchargedOnly, categoryFilter, brandFilter, dateFrom, dateTo]);

  const visibleRows = useMemo(
    () => filteredRows.slice(0, visibleCount),
    [filteredRows, visibleCount],
  );

  // Brand options for the filter — only brands actually present in the book.
  const brandOptions = useMemo(() => {
    const counts = new Map();
    for (const row of products) if (row.brand_label) counts.set(row.brand_label, (counts.get(row.brand_label) || 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [products]);

  // Counts per category for the pill labels (computed against the full set,
  // not the currently-filtered set, so the pills always show the totals).
  const categoryCounts = useMemo(() => {
    const counts = {};
    let lightingCount = 0;
    for (const row of products) {
      const c = getProductCategory(row);
      counts[c] = (counts[c] || 0) + 1;
      // Lights pill uses the inclusive description-based detector so it
      // surfaces lighting products even when their canonical_key is RAW.
      if (looksLikeLighting(row)) lightingCount += 1;
    }
    counts.LIGHT = lightingCount;
    return counts;
  }, [products]);

  const overchargedCount = useMemo(
    () => products.filter((row) => getOverchargePct(row) !== null).length,
    [products]
  );

  const summary = useMemo(() => {
    const latest = products.reduce((latestRow, row) => {
      const nextDate = new Date(row.latestDate || 0).getTime();
      const latestDate = new Date(latestRow?.latestDate || 0).getTime();
      return nextDate > latestDate ? row : latestRow;
    }, null);
    return {
      productCount: products.length,
      supplierPriceCount: products.reduce((sum, row) => sum + (row.supplierRows?.length || 0), 0),
      supplierCount: suppliers.length,
      latestDate: latest?.latestDate || null,
    };
  }, [products, suppliers.length]);

  const toggleSupplierView = useCallback((productId) => {
    setExpandedSuppliers((current) => ({ ...current, [productId]: !current[productId] }));
  }, []);

  const runInvoiceSync = useCallback(async () => {
    setSyncing(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "syncInvoicePrices" }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Invoice sync failed");
      setSyncReport(data);
      await Promise.all([loadPriceBook(), loadReviewQueue()]);
    } catch (err) {
      setError(err.message || "Invoice sync failed");
    } finally {
      setSyncing(false);
    }
  }, [loadPriceBook, loadReviewQueue]);

  const resolveReviewItem = useCallback(async (reviewItem, resolution) => {
    const reviewKey = [
      normalize(reviewItem.supplier),
      normalize(reviewItem.description),
      normalize(reviewItem.invoiceNumber),
      Number(reviewItem.newPrice) || "",
    ].join("|");
    setResolvingKey(reviewKey);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "resolveReviewItem",
          reviewKey,
          resolution,
          manualPrice: manualPrices[reviewKey],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Could not resolve review item");
      await Promise.all([loadPriceBook(), loadReviewQueue()]);
    } catch (err) {
      setError(err.message || "Could not resolve review item");
    } finally {
      setResolvingKey("");
    }
  }, [loadPriceBook, loadReviewQueue, manualPrices]);

  const openMergeModal = useCallback((product) => {
    setMergeSource(product);
    setMergeSearch(product?.description || "");
    setMergeResults([]);
    setOpenMenuId("");
  }, []);

  const runMergeSearch = useCallback(async (term, excludeId) => {
    setMergeSearching(true);
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "searchProducts", term, excludeId, limit: 25 }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Search failed");
      setMergeResults(data.products || []);
    } catch (err) {
      setError(err.message || "Merge search failed");
    } finally {
      setMergeSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!mergeSource) return;
    const handle = setTimeout(() => runMergeSearch(mergeSearch, mergeSource.id), 200);
    return () => clearTimeout(handle);
  }, [mergeSearch, mergeSource, runMergeSearch]);

  const confirmMerge = useCallback(async (target) => {
    if (!mergeSource || !target) return;
    if (!window.confirm(`Merge "${mergeSource.description}" into "${target.description}"?\n\nAll supplier prices and aliases will move. The source product will be deleted.`)) return;
    setMergeBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "mergeProducts", fromId: mergeSource.id, toId: target.id }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Merge failed");
      setActionMessage(`Merged: ${data.movedPriceCount} prices and ${data.movedAliasCount} aliases moved.`);
      setMergeSource(null);
      setMergeResults([]);
      setMergeSearch("");
      await loadPriceBook();
    } catch (err) {
      setError(err.message || "Merge failed");
    } finally {
      setMergeBusy(false);
    }
  }, [mergeSource, loadPriceBook]);

  const splitSupplier = useCallback(async (product, supplierName) => {
    if (!product || !supplierName) return;
    if (!window.confirm(`Split "${supplierName}" prices off "${product.description}" into a new product?`)) return;
    const splitKey = `${product.id}|${supplierName}`;
    setSplitBusy(splitKey);
    setError("");
    setOpenMenuId("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "splitProduct", sourceId: product.id, supplierName }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Split failed");
      setActionMessage(`Split: ${data.movedPriceCount} prices moved to a new product.`);
      await loadPriceBook();
    } catch (err) {
      setError(err.message || "Split failed");
    } finally {
      setSplitBusy("");
    }
  }, [loadPriceBook]);

  const startEdit = useCallback((product) => {
    setEditing({
      id: product.id,
      description: product.description || "",
      sku: product.sku || "",
      category: product.category || "",
      brandCode: product.brand || "",       // persisted group key ("" = auto)
      brandLabel: product.brand_label || "", // display, incl. derived
      // Only prefill a MANUALLY-set part number. A derived one is shown on the
      // card but left blank here, so saving the form doesn't silently freeze a
      // guess into place as though a human had confirmed it.
      mfgPartNumber: product.mfg_part_source === "manual" ? (product.mfg_part_number || "") : "",
    });
    setOpenMenuId("");
  }, []);

  const deleteProduct = useCallback(async (product) => {
    if (!product?.id) return;
    if (!window.confirm(`Delete "${product.description || product.sku}" and all its supplier prices?\n\nThis is irreversible. (Audit row will be saved so it can be reconstructed if needed.)`)) return;
    setSplitBusy(`delete|${product.id}`);
    setError("");
    setOpenMenuId("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "deleteProduct", productId: product.id }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Delete failed");
      setActionMessage(`Deleted: ${data.deletedPriceCount} supplier prices removed.`);
      await loadPriceBook();
    } catch (err) {
      setError(err.message || "Delete failed");
    } finally {
      setSplitBusy("");
    }
  }, [loadPriceBook]);

  const blockSupplier = useCallback(async (product, supplierName) => {
    if (!product || !supplierName) return;
    if (!window.confirm(
      `Block "${supplierName}" from the Price List?\n\n` +
      `Their existing prices on this product will be removed, and future invoices from them won't create price entries. ` +
      `Use this for non-material suppliers (advertising, services, software, etc).`
    )) return;
    const blockKey = `block|${product.id}|${supplierName}`;
    setSplitBusy(blockKey);
    setError("");
    setOpenMenuId("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "blockSupplier", supplierName, purgeExisting: true }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Block failed");
      setActionMessage(
        `Blocked ${supplierName}: ${data.purgedPriceCount} prices removed${data.purgedProductCount ? `, ${data.purgedProductCount} orphan products deleted` : ""}. Future invoices from this supplier will be ignored.`
      );
      await loadPriceBook();
    } catch (err) {
      setError(err.message || "Block failed");
    } finally {
      setSplitBusy("");
    }
  }, [loadPriceBook]);

  const runBackfill = useCallback(async () => {
    setBackfillBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "backfillPriceSources", limit: 2000, invoiceLimit: 2000 }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Backfill failed");
      setBackfillReport(data);
      await loadPriceBook();
    } catch (err) {
      setError(err.message || "Backfill failed");
    } finally {
      setBackfillBusy(false);
    }
  }, [loadPriceBook]);

  const runPurgeOrphans = useCallback(async (apply = false) => {
    setOrphanBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "purgeOrphanedProducts", dryRun: !apply }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Orphan purge failed");
      setOrphanReport(data);
      if (apply) await loadPriceBook();
    } catch (err) {
      setError(err.message || "Orphan purge failed");
    } finally {
      setOrphanBusy(false);
    }
  }, [loadPriceBook]);

  const runPurgeNonMaterial = useCallback(async (apply = false) => {
    setPurgeBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "purgeNonMaterialPrices", dryRun: !apply }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Purge failed");
      setPurgeReport(data);
      if (apply) await loadPriceBook();
    } catch (err) {
      setError(err.message || "Purge failed");
    } finally {
      setPurgeBusy(false);
    }
  }, [loadPriceBook]);

  const runRecanonicalize = useCallback(async (apply = false) => {
    setRecanonBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "recanonicalizeProducts", dryRun: !apply }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Recanonicalize failed");
      setRecanonReport(data);
      if (apply) await loadPriceBook();
    } catch (err) {
      setError(err.message || "Recanonicalize failed");
    } finally {
      setRecanonBusy(false);
    }
  }, [loadPriceBook]);

  // Pack-price repair — the "washer problem": per-pack prices stored as
  // per-each (100/PK, /JAR, per-C lines). Dry run first (packAudit), then
  // Apply runs packFix: fixes land in pricebook_fix_log (reversible) and
  // anything uncertain goes to the Review Queue.
  const runPackRepair = useCallback(async (apply = false) => {
    setPackBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: apply ? "packFix" : "packAudit", applyMedium: true, aiLimit: 120 }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Pack repair failed");
      setPackReport(data);
      if (apply) await loadPriceBook();
    } catch (err) {
      setError(err.message || "Pack repair failed");
    } finally {
      setPackBusy(false);
    }
  }, [loadPriceBook]);

  const runCsvBackfill = useCallback(async (apply = false) => {
    setCsvBackfillBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "backfillCsvSources", dryRun: !apply }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "CSV backfill failed");
      setCsvBackfillReport(data);
      if (apply) await loadPriceBook();
    } catch (err) {
      setError(err.message || "CSV backfill failed");
    } finally {
      setCsvBackfillBusy(false);
    }
  }, [loadPriceBook]);

  const viewSourceInvoice = useCallback(async (productRow, supplierRow, supplierSummaryEntry) => {
    const supplierName = supplierRow?.supplier_name || supplierSummaryEntry?.supplier_name || "";
    const supplierSku = supplierRow?.supplier_sku || supplierSummaryEntry?.supplier_sku || "";
    const supplierDescription = supplierRow?.supplier_description || supplierSummaryEntry?.supplier_description || productRow?.description || "";
    const supplierPriceId = supplierRow?.id || supplierSummaryEntry?.supplier_price_id || "";
    const sourceType = supplierRow?.source_type || supplierSummaryEntry?.source_type || "";
    const cachedPdfUrl = supplierRow?.source_pdf_url || supplierSummaryEntry?.source_pdf_url || "";

    // FAST PATH 1: cached invoice PDF URL → open in zero round trips.
    if (cachedPdfUrl) {
      window.open(cachedPdfUrl, "_blank", "noopener");
      return;
    }

    // FAST PATH 2: tagged as csv_import → fetch the import_log row and show
    // a provenance modal. No PDF exists; the source is a CSV upload.
    if (sourceType === "csv_import") {
      const key = `${productRow?.id}|${supplierName}`;
      setInvoiceLoadingKey(key);
      setError("");
      try {
        const res = await apiFetch("/api/pricebook", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "getPriceSource",
            supplierPriceId,
            masterProductId: productRow?.id,
            supplierName,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.ok === false) throw new Error(data.error || "Could not load CSV source");
        setCsvSourceModal({
          ...data,
          productDescription: productRow?.description,
          supplierName,
        });
      } catch (err) {
        setError(err.message || "Could not load CSV source");
      } finally {
        setInvoiceLoadingKey("");
      }
      return;
    }

    const key = `${productRow?.id}|${supplierName}`;
    setInvoiceLoadingKey(key);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "findInvoiceForPrice",
          // Pass supplierPriceId so the backend takes the FAST PATH (direct
          // lookup by source_invoice_id). Falls back to fuzzy search for
          // legacy rows that haven't been backfilled yet.
          supplierPriceId,
          masterProductId: productRow?.id,
          supplierName,
          supplierSku,
          supplierDescription,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) {
        const diag = data.diagnostics;
        const detail = diag
          ? ` (scanned ${diag.totalInvoicesScanned} invoices, ${diag.matchingSupplierInvoiceCount} from a matching supplier${diag.distinctSupplierNamesNearby?.length ? `; nearby names: ${diag.distinctSupplierNamesNearby.join(", ")}` : ""})`
          : "";
        throw new Error(`${data.error || "Could not find source invoice"}${detail}`);
      }

      const inv = data.invoice;
      // Happy path: a public PDF URL exists — open it directly in a new tab.
      if (inv?.pdfUrl) {
        window.open(inv.pdfUrl, "_blank", "noopener");
        setInvoicePreview({ ...inv, matchedLine: data.matchedLine, opened: true });
        return;
      }

      // Fallback: older scan-receipt rows have a data-URI receipt_image.
      // Fetch it and open as a blob URL so it works in a new tab.
      if (inv?.hasReceiptImage) {
        const imgRes = await apiFetch("/api/pricebook", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "getInvoiceImage", invoiceId: inv.id }),
        });
        const imgData = await imgRes.json();
        if (imgData?.ok && imgData.receiptImage) {
          window.open(imgData.receiptImage, "_blank", "noopener");
          setInvoicePreview({ ...inv, matchedLine: data.matchedLine, opened: true, openedAs: "image" });
          return;
        }
      }

      // No PDF and no image — show what we found in a small modal so Dylan
      // can at least see the invoice number / line context.
      setInvoicePreview({ ...inv, matchedLine: data.matchedLine, opened: false });
    } catch (err) {
      setError(err.message || "Could not open source invoice");
    } finally {
      setInvoiceLoadingKey("");
    }
  }, []);

  // Available suppliers for the negotiation report dropdown.
  const allSuppliers = useMemo(() => {
    const set = new Set();
    for (const row of products) {
      for (const sup of (row.supplierRows || [])) if (sup.supplier_name) set.add(sup.supplier_name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  // Build the negotiation report against a target supplier and threshold:
  // for every product where the target supplier's price is X% above the
  // cheapest other supplier on the same product, emit a row.
  const buildNegotiationRows = useCallback((targetSupplier, thresholdPct, scopeCategory) => {
    if (!targetSupplier) return [];
    const rows = [];
    for (const product of products) {
      if (scopeCategory && scopeCategory !== "ALL" && getProductCategory(product) !== scopeCategory) continue;
      const supRows = product.supplierRows || [];
      if (supRows.length < 2) continue;
      const targetRow = supRows.find((r) => r.supplier_name === targetSupplier);
      if (!targetRow) continue;
      const competitorRows = supRows.filter((r) => r.supplier_name !== targetSupplier);
      const cheapest = competitorRows.reduce((best, r) => {
        const v = Number(r.unit_price);
        if (!Number.isFinite(v) || v <= 0) return best;
        if (!best || v < Number(best.unit_price)) return r;
        return best;
      }, null);
      if (!cheapest) continue;
      const targetPrice = Number(targetRow.unit_price);
      const cheapestPrice = Number(cheapest.unit_price);
      if (!Number.isFinite(targetPrice) || !Number.isFinite(cheapestPrice) || cheapestPrice <= 0) continue;
      const overPct = (targetPrice - cheapestPrice) / cheapestPrice;
      if (overPct < thresholdPct / 100) continue;
      rows.push({
        sku: product.sku,
        description: product.description,
        category: getProductCategoryLabel(product),
        targetPrice,
        cheapestPrice,
        cheapestSupplier: cheapest.supplier_name,
        diffDollar: targetPrice - cheapestPrice,
        diffPct: overPct,
        targetDate: targetRow.date_recorded,
        cheapestDate: cheapest.date_recorded,
      });
    }
    rows.sort((a, b) => b.diffPct - a.diffPct);
    return rows;
  }, [products]);

  const downloadCsv = useCallback((filename, headers, rows) => {
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const submitEdit = useCallback(async () => {
    if (!editing?.id) return;
    setEditBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/pricebook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "editProduct",
          productId: editing.id,
          description: editing.description,
          sku: editing.sku,
          category: editing.category,
          brand: editing.brandCode ?? "",
          mfgPartNumber: editing.mfgPartNumber ?? "",
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Edit failed");
      setActionMessage("Product updated.");
      setEditing(null);
      await loadPriceBook();
    } catch (err) {
      setError(err.message || "Edit failed");
    } finally {
      setEditBusy(false);
    }
  }, [editing, loadPriceBook]);

  return (
    <>
      <style>{PRICEBOOK_CSS}</style>
      <div className="pb-wrap">
        <div className="pb-header">
          <div>
            <div className="pb-title">Price List</div>
            <div className="pb-subtitle">Master pricing for quotes, invoices, and Jai-Man lookups</div>
          </div>
          <div className="pb-actions">
            {isAdmin && (
              <>
                <button className="pb-btn pb-btn-accent" onClick={runInvoiceSync} disabled={syncing}>
                  {syncing ? "Syncing..." : "Sync From Invoices"}
                </button>
                <button className="pb-btn" onClick={() => setShowReportModal(true)} title="Build a CSV showing where one supplier is materially more expensive than another — to send to the more expensive one.">
                  Negotiation Report
                </button>
                <button className="pb-btn" onClick={() => setShowCompareModal(true)} title="What each supplier actually billed us over a period, compared item-by-item against other suppliers.">
                  Supplier Comparison
                </button>
                <div className="pb-admin-wrap">
                  <button className="pb-btn" onClick={() => setShowAdminMenu((v) => !v)} title="Maintenance actions">
                    Admin {showAdminMenu ? "▴" : "▾"}
                  </button>
                  {showAdminMenu && (
                    <div className="pb-admin-menu" onClick={(event) => event.stopPropagation()}>
                      <div className="pb-admin-section">Audit & repair</div>
                      <button onClick={() => { setShowAdminMenu(false); runPurgeNonMaterial(false); }} disabled={purgeBusy} title="Remove subcontractor / service / software invoices (Veritas, MJK, Anthropic, Kal Tire, etc).">
                        {purgeBusy ? "Scanning…" : "Purge non-material prices"}
                      </button>
                      <button onClick={() => { setShowAdminMenu(false); runPurgeOrphans(false); }} disabled={orphanBusy} title="Remove master_products that have no supplier_prices linked.">
                        {orphanBusy ? "Scanning…" : "Purge orphaned products"}
                      </button>
                      <button onClick={() => { setShowAdminMenu(false); runPackRepair(false); }} disabled={packBusy} title="Find per-pack prices stored as per-each (100/PK washers, per-C lines) and repair them from the source invoice + cross-supplier twins. Dry run first; uncertain rows go to the Review Queue.">
                        {packBusy ? "Auditing…" : "Repair pack prices"}
                      </button>
                      <div className="pb-admin-section">Categorization</div>
                      <button onClick={() => { setShowAdminMenu(false); runRecanonicalize(false); }} disabled={recanonBusy} title="Recompute canonical_key for every product using the current rules. Use after expanding the canonicalizer (e.g. adding Lighting) so existing rows pick up the new categories.">
                        {recanonBusy ? "Scanning…" : "Recanonicalize products"}
                      </button>
                      <div className="pb-admin-section">Source linking</div>
                      <button onClick={() => { setShowAdminMenu(false); runBackfill(); }} disabled={backfillBusy} title="One-shot pass that links existing supplier prices to their source invoice PDFs.">
                        {backfillBusy ? "Linking…" : "Link PDFs to old prices"}
                      </button>
                      <button onClick={() => { setShowAdminMenu(false); runCsvBackfill(false); }} disabled={csvBackfillBusy} title="Tag prices that came from a CSV upload with their import_log row.">
                        {csvBackfillBusy ? "Tagging…" : "Tag CSV-imported prices"}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            <button className="pb-btn" onClick={loadPriceBook} disabled={loading}>Refresh</button>
          </div>
        </div>

        <div className="pb-stats">
          <div className="pb-stat"><strong>{summary.productCount}</strong><span>Products</span></div>
          <div className="pb-stat"><strong>{summary.supplierPriceCount}</strong><span>Supplier Prices</span></div>
          <div className="pb-stat"><strong>{summary.supplierCount}</strong><span>Suppliers</span></div>
          <div className="pb-stat"><strong>{reviewItems.length}</strong><span>Need Review</span></div>
        </div>

        <div className="pb-tabs">
          <button className={`pb-tab ${activeTab === "browse" ? "active" : ""}`} onClick={() => setActiveTab("browse")}>Browse</button>
          {/* Review Queue + Manual Import are admin-only — they edit pricing data. */}
          {isAdmin && (
            <>
              <button className={`pb-tab ${activeTab === "review" ? "active" : ""}`} onClick={() => setActiveTab("review")}>Review Queue</button>
              <button className={`pb-tab ${activeTab === "import" ? "active" : ""}`} onClick={() => setActiveTab("import")}>Manual Import</button>
            </>
          )}
        </div>

        {error && <div className="pb-message error">{error}</div>}
        {actionMessage && <div className="pb-message">{actionMessage}</div>}
        {syncReport && (
          <div className="pb-message">
            {syncReport.processedLineItems} line items checked, {syncReport.updatedPriceCount} prices updated, {syncReport.createdCount} new products created, {syncReport.learnedAliasCount || 0} aliases learned, {syncReport.flaggedCount} flagged for review{syncReport.autoCleanupFixCount > 0 ? `, ${syncReport.autoCleanupFixCount} math-error prices auto-corrected` : ""}.
          </div>
        )}
        {orphanReport && (
          <div className="pb-message">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginBottom:8}}>
              <strong>
                {orphanReport.dryRun ? "Orphan dry run" : "Orphans purged"}: {orphanReport.orphanCount} master_products have no supplier_prices ({orphanReport.autoOrphanCount} auto-synced like MJK/labor lines, {orphanReport.manualOrphanCount} manual/CSV).
              </strong>
              {orphanReport.dryRun && orphanReport.orphanCount > 0 && (
                <button className="pb-btn pb-btn-accent" onClick={() => {
                  if (window.confirm(`Delete ${orphanReport.orphanCount} master_products with no prices? This is irreversible but they have no actual pricing data attached.`)) {
                    runPurgeOrphans(true);
                  }
                }} disabled={orphanBusy}>Apply orphan purge</button>
              )}
              <button className="pb-link-btn" onClick={() => setOrphanReport(null)}>Dismiss</button>
            </div>
            {orphanReport.sampleAutoOrphans?.length > 0 && (
              <div style={{maxHeight:180,overflow:"auto",fontSize:11,color:"var(--text-muted,#8892a4)",lineHeight:1.5}}>
                <div><strong>Sample auto-synced orphans:</strong></div>
                {orphanReport.sampleAutoOrphans.map((p, i) => (
                  <div key={i}>{p.sku} - {p.description}</div>
                ))}
              </div>
            )}
          </div>
        )}
        {purgeReport && (
          <div className="pb-message">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginBottom:8}}>
              <strong>
                {purgeReport.dryRun ? "Purge dry run" : "Purge applied"}: {purgeReport.priceDeleteCount} non-material prices found across {purgeReport.affectedProductCount} products. {purgeReport.orphanedProductDeleteCount} master_products would be left orphaned and {purgeReport.dryRun ? "would" : "were"} also deleted.
              </strong>
              {purgeReport.dryRun && purgeReport.priceDeleteCount > 0 && (
                <button className="pb-btn pb-btn-accent" onClick={() => {
                  if (window.confirm(`Delete ${purgeReport.priceDeleteCount} prices and ${purgeReport.orphanedProductDeleteCount} orphaned products? This is irreversible.`)) {
                    runPurgeNonMaterial(true);
                  }
                }} disabled={purgeBusy}>Apply purge</button>
              )}
              <button className="pb-link-btn" onClick={() => setPurgeReport(null)}>Dismiss</button>
            </div>
            {purgeReport.supplierBreakdown && Object.keys(purgeReport.supplierBreakdown).length > 0 && (
              <div style={{fontSize:11,color:"var(--text-muted,#8892a4)",lineHeight:1.6}}>
                Breakdown:{" "}
                {Object.entries(purgeReport.supplierBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([name, count]) => `${name} (${count})`)
                  .join(", ")}
              </div>
            )}
          </div>
        )}
        {recanonReport && (
          <div className="pb-message">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginBottom:8}}>
              <strong>
                {recanonReport.dryRun ? "Recanonicalize dry run" : "Recanonicalize applied"}: {recanonReport.changedCount} of {recanonReport.scanned} products would change category{recanonReport.changedCount > 0 && Object.keys(recanonReport.breakdown || {}).length > 0 ? ` (${Object.entries(recanonReport.breakdown).sort((a,b) => b[1]-a[1]).map(([k,v]) => `${k}: ${v}`).join(", ")})` : ""}.
              </strong>
              {recanonReport.dryRun && recanonReport.changedCount > 0 && (
                <button className="pb-btn pb-btn-accent" onClick={() => {
                  if (window.confirm(`Update canonical_key on ${recanonReport.changedCount} products? This is reversible (re-running recategorizes again from descriptions) but may regroup how products appear under the category pills.`)) {
                    runRecanonicalize(true);
                  }
                }} disabled={recanonBusy}>Apply</button>
              )}
              <button className="pb-link-btn" onClick={() => setRecanonReport(null)}>Dismiss</button>
            </div>
            {recanonReport.samples?.length > 0 && (
              <div style={{maxHeight:180,overflow:"auto",fontSize:11,color:"var(--text-muted,#8892a4)",lineHeight:1.5}}>
                <div><strong>Sample changes:</strong></div>
                {recanonReport.samples.slice(0, 15).map((s, i) => (
                  <div key={i}>{s.description} — {s.oldKey || "(no key)"} → {s.newKey}</div>
                ))}
              </div>
            )}
          </div>
        )}
        {packReport && (
          <div className="pb-message">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginBottom:8}}>
              <strong>
                {packReport.dryRun ? "Pack-price repair dry run" : "Pack-price repair applied"}: {Number(packReport.totals?.priceChanges || 0)} price fixes, {Number(packReport.totals?.stampOnly || 0)} pack-size stamps, {Number(packReport.totals?.deleted || 0)} junk deletions, {Number(packReport.totals?.queuedForReview || 0)} queued for review — of {Number(packReport.totals?.rowsScanned || 0)} rows scanned.
                {!packReport.dryRun ? ` Applied ${Number(packReport.totals?.applied || 0)} changes (all logged in pricebook_fix_log — reversible).` : ""}
              </strong>
              {packReport.dryRun && (Number(packReport.totals?.priceChanges || 0) + Number(packReport.totals?.deleted || 0)) > 0 && (
                <button className="pb-btn pb-btn-accent" onClick={() => {
                  if (window.confirm(`Apply ${Number(packReport.totals?.priceChanges || 0)} price fixes and ${Number(packReport.totals?.deleted || 0)} junk deletions? Every change is logged with its old price in pricebook_fix_log, so it can be reversed.`)) {
                    runPackRepair(true);
                  }
                }} disabled={packBusy}>{packBusy ? "Applying…" : "Apply fixes"}</button>
              )}
              <button className="pb-link-btn" onClick={() => setPackReport(null)}>Dismiss</button>
            </div>
            {(packReport.proposals || []).filter((p) => p.newPrice != null).length > 0 && (
              <div style={{maxHeight:200,overflow:"auto",fontSize:11,color:"var(--text-muted,#8892a4)",lineHeight:1.5}}>
                <div><strong>Price fixes{packReport.dryRun ? " (proposed)" : ""}:</strong></div>
                {(packReport.proposals || []).filter((p) => p.newPrice != null).slice(0, 25).map((p, i) => (
                  <div key={i}>{p.supplier} — {p.description}: ${Number(p.oldPrice)} → ${Number(p.newPrice)}{p.packQty ? ` (pack of ${p.packQty})` : ""} · {p.confidence} · {p.reason}</div>
                ))}
              </div>
            )}
          </div>
        )}
        {csvBackfillReport && (
          <div className="pb-message">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginBottom:8}}>
              <strong>
                {csvBackfillReport.dryRun ? "CSV tag dry run" : "CSV tag applied"}: would link {csvBackfillReport.linkedExactly} prices to their import_log row, tag {csvBackfillReport.taggedAmbiguous} as csv_import (ambiguous — multiple imports for the supplier). Scanned {csvBackfillReport.scanned} unlinked prices, {csvBackfillReport.importLogCount} import_log rows.
              </strong>
              {csvBackfillReport.dryRun && (csvBackfillReport.linkedExactly + csvBackfillReport.taggedAmbiguous) > 0 && (
                <button className="pb-btn pb-btn-accent" onClick={() => runCsvBackfill(true)} disabled={csvBackfillBusy}>
                  Apply CSV tags
                </button>
              )}
              <button className="pb-link-btn" onClick={() => setCsvBackfillReport(null)}>Dismiss</button>
            </div>
            {csvBackfillReport.suppliersWithImports?.length > 0 && (
              <div style={{fontSize:11,color:"var(--text-muted,#8892a4)"}}>Suppliers with import_log rows: {csvBackfillReport.suppliersWithImports.join(", ")}</div>
            )}
          </div>
        )}
        {backfillReport && (
          <div className="pb-message">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
              <strong>
                Backfill: linked {backfillReport.updatedCount} of {backfillReport.scannedPrices} legacy prices to a source invoice (scanned {backfillReport.scannedInvoices} invoices).
                {backfillReport.scannedPrices > 0 && backfillReport.matchedCount < backfillReport.scannedPrices
                  ? ` ${backfillReport.scannedPrices - backfillReport.matchedCount} couldn't be matched — their source invoice may be older than the scan window.`
                  : ""}
              </strong>
              <button className="pb-link-btn" onClick={() => setBackfillReport(null)}>Dismiss</button>
            </div>
          </div>
        )}
        {activeTab === "browse" && (
          <div className="pb-body">
            <div className="pb-search">
              <input
                placeholder="Search material, trade name, part, supplier, or invoice wording"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="pb-cat-row">
              {[
                { code: "ALL", label: "All" },
                { code: "WIRE", label: "Wire & Cable" },
                { code: "CONDUIT", label: "Conduit" },
                { code: "BOX", label: "Boxes & Covers" },
                { code: "BREAKER", label: "Breakers" },
                { code: "DEVICE", label: "Devices" },
                { code: "LIGHT", label: "Lights" },
                { code: "CONNECTOR", label: "Connectors" },
                { code: "TOOL", label: "Tools" },
                { code: "FASTENER", label: "Fasteners" },
                { code: "DATA", label: "Low-Volt / Data" },
                { code: "SOLAR", label: "Solar" },
                // alwaysShow: pin the pill in the row even when count is 0,
                // so the user can see the bucket exists before importing into it.
                { code: "GENERATOR", label: "Generators", alwaysShow: true },
                { code: "MINISPLIT", label: "Mini Splits", alwaysShow: true },
                { code: "MANUAL", label: "Manual category" },
                { code: "RAW", label: "Other" },
              ].map((cat) => {
                const count = cat.code === "ALL" ? products.length : (categoryCounts[cat.code] || 0);
                if (cat.code !== "ALL" && count === 0 && !cat.alwaysShow) return null;
                const active = categoryFilter === cat.code;
                return (
                  <button
                    type="button"
                    key={cat.code}
                    className={`pb-cat-pill ${active ? "active" : ""}`}
                    onClick={() => setCategoryFilter(cat.code)}
                  >
                    {cat.label} <em>{count}</em>
                  </button>
                );
              })}
            </div>
            <div className="pb-filter-row">
              <label className="pb-filter-toggle">
                <input
                  type="checkbox"
                  checked={showOverchargedOnly}
                  onChange={(event) => setShowOverchargedOnly(event.target.checked)}
                />
                <span>Show overcharged items only</span>
                <em>{overchargedCount}</em>
              </label>
              {brandOptions.length > 0 && (
                <label className="pb-brand-filter">
                  Brand
                  <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} title="Filter breakers/panels by brand — Siemens ≠ Square D QO ≠ Eaton">
                    <option value="ALL">All</option>
                    {brandOptions.map(([label, n]) => <option key={label} value={label}>{label} ({n})</option>)}
                    <option value="__NONE__">— no brand —</option>
                  </select>
                </label>
              )}
              <div className="pb-date-filter">
                <label>From <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></label>
                <label>To <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></label>
                {(dateFrom || dateTo) && (
                  <button type="button" className="pb-link-btn" onClick={() => { setDateFrom(""); setDateTo(""); }}>Clear dates</button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="pb-empty">Loading price book...</div>
            ) : !filteredRows.length ? (
              <div className="pb-empty">No price book matches that search yet.</div>
            ) : (
              visibleRows.map((row) => {
                const overchargePct = getOverchargePct(row);
                return (
                <div className="pb-card" key={row.id}>
                  <div className="pb-card-top">
                    <div>
                      <div className="pb-card-title">
                        {row.description || row.sku}
                        {row.brand_label ? (
                          <span className="pb-brand-badge" title="Breaker/panel brand — a Siemens Q and a Square D QO are not interchangeable">
                            {row.brand_label}
                          </span>
                        ) : null}
                        {overchargePct !== null ? (
                          <span className="pb-overcharge-badge" title={`Latest from ${row.latestSupplier} is ${Math.round(overchargePct * 100)}% above best price (${row.bestSupplier})`}>
                            ⚠ {Math.round(overchargePct * 100)}% above best
                          </span>
                        ) : null}
                      </div>
                      <div className="pb-card-sub">{row.sku} - {row.category || "Uncategorized"} - {row.unit || "each"}</div>
                      {row.mfg_part_number ? (
                        <div className="pb-part-line">
                          <span
                            className="pb-part-badge"
                            title={row.mfg_part_source === "manual"
                              ? "Manufacturer part number — entered by hand"
                              : "Manufacturer part number — two suppliers independently print this same code, so it's the manufacturer's, not one wholesaler's"}
                          >
                            MFG #{row.mfg_part_number}
                          </span>
                        </div>
                      ) : null}
                      {row.tradeNames?.length ? (
                        <div className="pb-trade-names">
                          {row.tradeNames.slice(0, 6).map((tradeName) => <span key={tradeName}>{tradeName}</span>)}
                        </div>
                      ) : null}
                    </div>
                    <div className="pb-price-strip">
                      <div><span>Best</span><strong>{fmtCurrency(row.bestPrice)}</strong>{row.bestSupplier ? <em>{row.bestSupplier}</em> : null}</div>
                      <div><span>Avg</span><strong>{fmtCurrency(row.averagePrice)}</strong></div>
                      <div><span>Latest</span><strong>{fmtCurrency(row.latestPrice)}</strong>{row.latestDate ? <em>{fmtDate(row.latestDate)}</em> : null}</div>
                    </div>
                  </div>
                  <div className="pb-card-controls">
                    <button className="pb-link-btn" onClick={() => toggleSupplierView(row.id)}>
                      {expandedSuppliers[row.id] ? "Hide supplier columns" : "Show supplier columns"}
                    </button>
                    {/* Recategorize / Merge / Split / Edit are admin-only — they mutate the price book. */}
                    {isAdmin && <div className="pb-recat">
                      <button
                        type="button"
                        className="pb-link-btn"
                        onClick={() => setOpenMenuId((current) => (current === row.id ? "" : row.id))}
                      >
                        Recategorize {openMenuId === row.id ? "▴" : "▾"}
                      </button>
                      {openMenuId === row.id && (
                        <div className="pb-recat-menu">
                          <button type="button" onClick={() => openMergeModal(row)}>Merge into…</button>
                          <button type="button" onClick={() => startEdit(row)}>Edit name / category</button>
                          {row.supplierRows?.length > 1 && (
                            <div className="pb-recat-section">
                              <div className="pb-recat-label">Split off supplier:</div>
                              {Array.from(new Set((row.supplierRows || []).map((r) => r.supplier_name))).map((supplierName) => (
                                <button
                                  type="button"
                                  key={supplierName}
                                  onClick={() => splitSupplier(row, supplierName)}
                                  disabled={splitBusy === `${row.id}|${supplierName}`}
                                >
                                  {splitBusy === `${row.id}|${supplierName}` ? "Splitting…" : supplierName}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="pb-recat-section">
                            <div className="pb-recat-label">Block supplier from price list:</div>
                            {Array.from(new Set((row.supplierRows || []).map((r) => r.supplier_name))).filter(Boolean).map((supplierName) => (
                              <button
                                type="button"
                                key={`block-${supplierName}`}
                                onClick={() => blockSupplier(row, supplierName)}
                                disabled={splitBusy === `block|${row.id}|${supplierName}`}
                                title="Stop this supplier's invoices from creating price entries (e.g. advertising or services)"
                              >
                                {splitBusy === `block|${row.id}|${supplierName}` ? "Blocking…" : `Block ${supplierName}`}
                              </button>
                            ))}
                          </div>
                          <div className="pb-recat-section">
                            <button
                              type="button"
                              className="pb-recat-danger"
                              onClick={() => deleteProduct(row)}
                              disabled={splitBusy === `delete|${row.id}`}
                            >
                              {splitBusy === `delete|${row.id}` ? "Deleting…" : "Delete this product"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>}
                  </div>
                  <div className="pb-suppliers">
                    {row.supplierRows?.length ? row.supplierRows.slice(0, 6).map((supplierRow) => {
                      const loadingKey = `${row.id}|${supplierRow.supplier_name}`;
                      const isLoading = invoiceLoadingKey === loadingKey;
                      return (
                        <button
                          type="button"
                          className="pb-supplier-chip pb-supplier-chip-btn"
                          key={supplierRow.id}
                          onClick={() => viewSourceInvoice(row, supplierRow)}
                          disabled={isLoading}
                          title="Open source invoice PDF"
                        >
                          <strong>{supplierRow.supplier_name}</strong>
                          <span>
                            {fmtCurrency(supplierRow.unit_price)}
                            {Number(supplierRow.pack_qty) > 1 ? (
                              <span style={{color:"var(--text-muted,#8892a4)",fontSize:10}} title="Sold in packs — unit price shown is per piece"> ·{Number(supplierRow.pack_qty)}/pk</span>
                            ) : null}
                          </span>
                          <em>{isLoading ? "Opening…" : fmtDate(supplierRow.date_recorded || supplierRow.updated_at)}</em>
                        </button>
                      );
                    }) : <div className="pb-empty-inline">No supplier prices yet.</div>}
                  </div>
                  {expandedSuppliers[row.id] && row.supplierCount > 0 ? (
                    <div className="pb-supplier-table-wrap">
                      <div className="pb-supplier-table">
                        {suppliers.filter((name) => row.supplierSummary?.[name]).map((name) => {
                          const summary = row.supplierSummary[name];
                          const loadingKey = `${row.id}|${name}`;
                          const isLoading = invoiceLoadingKey === loadingKey;
                          return (
                            <button
                              type="button"
                              className="pb-supplier-col pb-supplier-col-btn"
                              key={`${row.id}-${name}`}
                              onClick={() => viewSourceInvoice(row, null, { supplier_name: name, supplier_sku: summary?.supplier_sku, supplier_description: summary?.supplier_description })}
                              disabled={isLoading}
                              title="Open source invoice PDF"
                            >
                              <div className="pb-supplier-col-head">{name}</div>
                              <div className="pb-supplier-col-price">{fmtCurrency(summary?.unit_price)}</div>
                              {/* The order code, and ONLY when it came off this
                                  supplier's own item column. supplier_sku is
                                  matcher food — showing it here would put
                                  invented values like "SEALING" in front of a
                                  tech who has no reason to doubt them. */}
                              {summary?.supplier_cat_code ? (
                                <div className="pb-supplier-col-code" title={`Order code at ${name} — as printed on their invoice`}>
                                  {summary.supplier_cat_code}
                                </div>
                              ) : (
                                <div className="pb-supplier-col-meta pb-supplier-col-nocode" title="This supplier's invoices haven't printed an item number we can trust for this part — check the source invoice before ordering by code.">
                                  No order code
                                </div>
                              )}
                              {summary?.supplier_title ? (
                                <div className="pb-supplier-col-meta" title={summary.supplier_description}>{summary.supplier_title}</div>
                              ) : null}
                              <div className="pb-supplier-col-meta">{isLoading ? "Opening…" : fmtDate(summary?.date_recorded)}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
                );
              })
            )}
            {filteredRows.length > visibleRows.length && (
              <div className="pb-more">
                <button
                  type="button"
                  className="pb-more-btn"
                  onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                >
                  Show {Math.min(PAGE_SIZE, filteredRows.length - visibleRows.length)} more
                </button>
                <span className="pb-more-count">
                  Showing {visibleRows.length} of {filteredRows.length}
                </span>
              </div>
            )}
          </div>
        )}

        {activeTab === "review" && (
          <div className="pb-body">
            {!reviewItems.length ? (
              <div className="pb-empty">No flagged price changes waiting for review.</div>
            ) : reviewItems.map((item) => {
              const reviewKey = [
                normalize(item.supplier),
                normalize(item.description),
                normalize(item.invoiceNumber),
                Number(item.newPrice) || "",
              ].join("|");
              return (
                <div className="pb-card" key={reviewKey}>
                  <div className="pb-card-top">
                    <div>
                      <div className="pb-card-title">{item.description}</div>
                      <div className="pb-card-sub">{item.supplier} - Inv {item.invoiceNumber ? <a href={`/expenses?search=${encodeURIComponent(item.invoiceNumber)}`} className="pb-inv-link" target="_blank" rel="noreferrer">{item.invoiceNumber}</a> : "-"} - {item.invoiceDate ? fmtDate(item.invoiceDate) : "-"}</div>
                      {(item.productDescription || item.supplierSkuCandidate || item.matchedBy || item.reviewReason || item.uom) ? (
                        <div className="pb-review-meta">
                          {item.productDescription ? <span>Matched to: {item.productDescription}{item.productSku ? ` (${item.productSku})` : ""}</span> : null}
                          {item.supplierSkuCandidate ? <span>Supplier SKU: {item.supplierSkuCandidate}</span> : null}
                          {item.matchedBy ? <span>Match path: {item.matchedBy}</span> : null}
                          {item.uom ? <span>UOM: {item.uom}</span> : null}
                          {Number.isFinite(Number(item.qty)) && Number(item.qty) > 0 ? <span>Qty: {item.qty}</span> : null}
                          {Number(item.total) > 0 ? <span>Line total: {fmtCurrency(item.total)}</span> : null}
                          {Number(item.rawUnitCost) > 0 ? <span>Stated unit cost: {fmtCurrency(item.rawUnitCost)}</span> : null}
                          {item.reviewReason ? <span style={{color:"#fca5a5"}}>{item.reviewReason}</span> : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="pb-price-strip">
                      <div><span>Current</span><strong>{fmtCurrency(item.existingPrice)}</strong>{item.lastRecorded ? <em>{fmtDate(item.lastRecorded)}</em> : null}</div>
                      <div><span>New</span><strong>{fmtCurrency(item.newPrice)}</strong></div>
                      <div><span>Action</span><strong>{item.masterProductId ? "Match found" : "Review"}</strong></div>
                    </div>
                  </div>
                  <div className="pb-review-row">
                    <input
                      className="pb-manual-input"
                      placeholder="Manual price"
                      value={manualPrices[reviewKey] ?? ""}
                      onChange={(event) => setManualPrices((current) => ({ ...current, [reviewKey]: event.target.value }))}
                    />
                    <button className="pb-btn pb-btn-accent" onClick={() => resolveReviewItem(item, "approve_new")} disabled={resolvingKey === reviewKey}>Use New Price</button>
                    <button className="pb-btn" onClick={() => resolveReviewItem(item, "keep_existing")} disabled={resolvingKey === reviewKey}>Keep Current</button>
                    <button className="pb-btn" onClick={() => resolveReviewItem(item, "manual_price")} disabled={resolvingKey === reviewKey || !manualPrices[reviewKey]}>Use Manual Price</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === "import" && <PriceImport />}

        {mergeSource && (
          <div className="pb-modal-backdrop" onClick={() => !mergeBusy && setMergeSource(null)}>
            <div className="pb-modal" onClick={(event) => event.stopPropagation()}>
              <div className="pb-modal-header">
                <div>
                  <div className="pb-modal-title">Merge into existing product</div>
                  <div className="pb-modal-sub">Source: {mergeSource.description} ({mergeSource.sku})</div>
                </div>
                <button type="button" className="pb-link-btn" onClick={() => setMergeSource(null)} disabled={mergeBusy}>Close</button>
              </div>
              <input
                className="pb-modal-search"
                placeholder="Search by description, SKU, or trade name"
                value={mergeSearch}
                onChange={(event) => setMergeSearch(event.target.value)}
                autoFocus
              />
              <div className="pb-modal-results">
                {mergeSearching ? (
                  <div className="pb-empty">Searching…</div>
                ) : !mergeResults.length ? (
                  <div className="pb-empty">No matches. Try a broader term.</div>
                ) : mergeResults.map((target) => (
                  <button
                    type="button"
                    key={target.id}
                    className="pb-modal-result"
                    onClick={() => confirmMerge(target)}
                    disabled={mergeBusy}
                  >
                    <div>
                      <div className="pb-modal-result-title">{target.description}</div>
                      <div className="pb-modal-result-sub">{target.sku} - {target.category || "Uncategorized"}</div>
                    </div>
                    <span className="pb-modal-pick">{mergeBusy ? "…" : "Merge"}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {showReportModal && (
          <NegotiationReportModal
            allSuppliers={allSuppliers}
            categoryFilter={categoryFilter}
            buildRows={buildNegotiationRows}
            onDownload={downloadCsv}
            onClose={() => setShowReportModal(false)}
          />
        )}

        {showCompareModal && (
          <SupplierComparisonModal
            allSuppliers={allSuppliers}
            onDownload={downloadCsv}
            onClose={() => setShowCompareModal(false)}
          />
        )}

        {csvSourceModal && (
          <div className="pb-modal-backdrop" onClick={() => setCsvSourceModal(null)}>
            <div className="pb-modal" onClick={(event) => event.stopPropagation()}>
              <div className="pb-modal-header">
                <div>
                  <div className="pb-modal-title">Imported from CSV</div>
                  <div className="pb-modal-sub">{csvSourceModal.supplierName} - {csvSourceModal.productDescription || "(no description)"}</div>
                </div>
                <button type="button" className="pb-link-btn" onClick={() => setCsvSourceModal(null)}>Close</button>
              </div>
              {csvSourceModal.import ? (
                <div style={{display:"flex",flexDirection:"column",gap:8,fontSize:13}}>
                  <div><strong>File:</strong> {csvSourceModal.import.fileName || "(no file name)"}</div>
                  <div><strong>Imported:</strong> {csvSourceModal.import.importedAt ? fmtDate(csvSourceModal.import.importedAt) : "(unknown)"}</div>
                  <div><strong>Total rows in file:</strong> {csvSourceModal.import.totalRows ?? "—"}</div>
                  <div><strong>Matched / updated / flagged:</strong> {csvSourceModal.import.matched ?? "—"} / {csvSourceModal.import.updated ?? "—"} / {csvSourceModal.import.flagged ?? "—"}</div>
                  <div style={{marginTop:8,padding:10,background:"rgba(13,15,18,.4)",border:"1px solid rgba(42,49,64,.55)",borderRadius:8,fontSize:12,color:"var(--text-muted,#8892a4)"}}>
                    This price came from a one-time CSV upload, not from a parsed invoice. To verify, open the original CSV file (kept on your computer where you uploaded it from).
                  </div>
                </div>
              ) : (
                <div className="pb-message">This price is tagged as csv_import but the matching import_log row couldn't be found (it may have been deleted, or the source_import_id wasn't captured at import time). Re-run the "Tag CSV-Imported Prices" backfill to attempt to link it.</div>
              )}
              {csvSourceModal.price && (
                <div style={{marginTop:8,fontSize:12,color:"var(--text-muted,#8892a4)"}}>
                  Stored as: {csvSourceModal.price.supplier_sku || "(no SKU)"} - {csvSourceModal.price.supplier_description || "(no description)"} @ {fmtCurrency(csvSourceModal.price.unit_price)} ({fmtDate(csvSourceModal.price.date_recorded)})
                </div>
              )}
            </div>
          </div>
        )}

        {invoicePreview && !invoicePreview.opened && (
          <div className="pb-modal-backdrop" onClick={() => setInvoicePreview(null)}>
            <div className="pb-modal" onClick={(event) => event.stopPropagation()}>
              <div className="pb-modal-header">
                <div>
                  <div className="pb-modal-title">Source invoice</div>
                  <div className="pb-modal-sub">{invoicePreview.supplier} - Inv {invoicePreview.invoiceNumber || "(no number)"} - {fmtDate(invoicePreview.invoiceDate)}</div>
                </div>
                <button type="button" className="pb-link-btn" onClick={() => setInvoicePreview(null)}>Close</button>
              </div>
              <div className="pb-message">No PDF or image stored for this invoice. Here's the matched line from the parsed data:</div>
              {invoicePreview.matchedLine && (
                <div style={{display:"flex",flexDirection:"column",gap:6,fontSize:13}}>
                  <div><strong>Description:</strong> {invoicePreview.matchedLine.description || "(none)"}</div>
                  {invoicePreview.matchedLine.sku && <div><strong>SKU:</strong> {invoicePreview.matchedLine.sku}</div>}
                  <div><strong>Qty:</strong> {invoicePreview.matchedLine.qty ?? "(unknown)"} {invoicePreview.matchedLine.uom || ""}</div>
                  <div><strong>Stated unit cost:</strong> {fmtCurrency(invoicePreview.matchedLine.unitCost)}</div>
                  <div><strong>Line total:</strong> {fmtCurrency(invoicePreview.matchedLine.total)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {editing && (
          <div className="pb-modal-backdrop" onClick={() => !editBusy && setEditing(null)}>
            <div className="pb-modal" onClick={(event) => event.stopPropagation()}>
              <div className="pb-modal-header">
                <div className="pb-modal-title">Edit product</div>
                <button type="button" className="pb-link-btn" onClick={() => setEditing(null)} disabled={editBusy}>Close</button>
              </div>
              <label className="pb-modal-label">Description
                <input
                  className="pb-modal-input"
                  value={editing.description}
                  onChange={(event) => setEditing((current) => ({ ...current, description: event.target.value }))}
                />
              </label>
              <label className="pb-modal-label">SKU
                <input
                  className="pb-modal-input"
                  value={editing.sku}
                  onChange={(event) => setEditing((current) => ({ ...current, sku: event.target.value }))}
                />
              </label>
              <label className="pb-modal-label">Category
                <input
                  className="pb-modal-input"
                  value={editing.category}
                  onChange={(event) => setEditing((current) => ({ ...current, category: event.target.value }))}
                />
              </label>
              <label className="pb-modal-label">Manufacturer part #
                <input
                  className="pb-modal-input"
                  value={editing.mfgPartNumber || ""}
                  placeholder="e.g. Q250 — leave blank to derive from suppliers"
                  onChange={(event) => setEditing((current) => ({ ...current, mfgPartNumber: event.target.value }))}
                />
                <span className="pb-modal-hint">
                  What the manufacturer calls this part, not what one supplier calls it. Set by hand here it
                  outranks anything derived. Blank = derive it when two suppliers agree on a code.
                </span>
              </label>
              <label className="pb-modal-label">Brand (breakers/panels)
                <select
                  className="pb-modal-input"
                  value={editing.brandCode || ""}
                  onChange={(event) => setEditing((current) => ({ ...current, brandCode: event.target.value }))}
                >
                  <option value="">Auto-detect{editing.brandLabel && !editing.brandCode ? ` (→ ${editing.brandLabel})` : ""}</option>
                  {BRAND_CHOICES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                </select>
              </label>
              <div className="pb-modal-actions">
                <button type="button" className="pb-btn" onClick={() => setEditing(null)} disabled={editBusy}>Cancel</button>
                <button type="button" className="pb-btn pb-btn-accent" onClick={submitEdit} disabled={editBusy}>{editBusy ? "Saving…" : "Save"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function NegotiationReportModal({ allSuppliers, categoryFilter, buildRows, onDownload, onClose }) {
  const [targetSupplier, setTargetSupplier] = useState(allSuppliers[0] || "");
  const [thresholdPct, setThresholdPct] = useState(10);
  const [scopeCategory, setScopeCategory] = useState(categoryFilter || "ALL");
  const rows = useMemo(() => buildRows(targetSupplier, thresholdPct, scopeCategory), [buildRows, targetSupplier, thresholdPct, scopeCategory]);
  const totalSavings = rows.reduce((s, r) => s + r.diffDollar, 0);

  const handleDownload = () => {
    onDownload(
      `negotiation-${(targetSupplier || "supplier").toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`,
      ["SKU", "Description", "Category", `${targetSupplier} price`, `${targetSupplier} date`, "Cheapest competitor", "Cheapest price", "Cheapest date", "Diff $", "Diff %"],
      rows.map((r) => [
        r.sku,
        r.description,
        r.category,
        r.targetPrice.toFixed(2),
        r.targetDate || "",
        r.cheapestSupplier,
        r.cheapestPrice.toFixed(2),
        r.cheapestDate || "",
        r.diffDollar.toFixed(2),
        (r.diffPct * 100).toFixed(1) + "%",
      ])
    );
  };

  return (
    <div className="pb-modal-backdrop" onClick={onClose}>
      <div className="pb-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth:720,width:"95%"}}>
        <div className="pb-modal-header">
          <div>
            <div className="pb-modal-title">Negotiation report</div>
            <div className="pb-modal-sub">Find products where your chosen supplier is materially more expensive than the cheapest alternative.</div>
          </div>
          <button type="button" className="pb-link-btn" onClick={onClose}>Close</button>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          <label className="pb-modal-label">Supplier to negotiate with
            <select className="pb-modal-input" value={targetSupplier} onChange={(e) => setTargetSupplier(e.target.value)}>
              {allSuppliers.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="pb-modal-label">Min markup vs cheapest
            <select className="pb-modal-input" value={thresholdPct} onChange={(e) => setThresholdPct(Number(e.target.value))}>
              <option value={5}>5%</option>
              <option value={10}>10%</option>
              <option value={15}>15%</option>
              <option value={20}>20%</option>
              <option value={30}>30%</option>
              <option value={50}>50%</option>
            </select>
          </label>
          <label className="pb-modal-label">Category
            <select className="pb-modal-input" value={scopeCategory} onChange={(e) => setScopeCategory(e.target.value)}>
              <option value="ALL">All categories</option>
              <option value="WIRE">Wire & Cable</option>
              <option value="CONDUIT">Conduit</option>
              <option value="BOX">Boxes</option>
              <option value="BREAKER">Breakers</option>
              <option value="DEVICE">Devices</option>
              <option value="CONNECTOR">Connectors</option>
              <option value="GENERATOR">Generators</option>
              <option value="MINISPLIT">Mini Splits</option>
            </select>
          </label>
        </div>

        <div className="pb-message" style={{marginTop:8}}>
          <strong>{rows.length}</strong> product{rows.length === 1 ? "" : "s"} where {targetSupplier || "the selected supplier"} is at least {thresholdPct}% above the cheapest alternative.
          {rows.length > 0 && <span> Potential per-unit savings if matched: <strong>${totalSavings.toFixed(2)}</strong>.</span>}
        </div>

        <div style={{maxHeight:"40vh",overflow:"auto",fontSize:12,marginTop:4}}>
          {rows.length === 0 ? (
            <div className="pb-empty">No products meet the threshold. Try lowering the % or expanding the category.</div>
          ) : (
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{textAlign:"left",color:"var(--text-muted,#8892a4)",fontSize:11}}>
                  <th style={{padding:"6px 4px"}}>Product</th>
                  <th style={{padding:"6px 4px",textAlign:"right"}}>{targetSupplier}</th>
                  <th style={{padding:"6px 4px"}}>Cheapest</th>
                  <th style={{padding:"6px 4px",textAlign:"right"}}>Diff</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 50).map((r, i) => (
                  <tr key={i} style={{borderTop:"1px solid rgba(42,49,64,.55)"}}>
                    <td style={{padding:"6px 4px"}}>{r.description?.slice(0, 50)}<br/><span style={{color:"var(--text-muted,#8892a4)",fontSize:10}}>{r.sku} - {r.category}</span></td>
                    <td style={{padding:"6px 4px",textAlign:"right"}}>${r.targetPrice.toFixed(2)}</td>
                    <td style={{padding:"6px 4px"}}>${r.cheapestPrice.toFixed(2)}<br/><span style={{color:"var(--text-muted,#8892a4)",fontSize:10}}>{r.cheapestSupplier}</span></td>
                    <td style={{padding:"6px 4px",textAlign:"right",color:"#fca5a5"}}>+{(r.diffPct * 100).toFixed(0)}%<br/><span style={{fontSize:10}}>${r.diffDollar.toFixed(2)}</span></td>
                  </tr>
                ))}
                {rows.length > 50 && <tr><td colSpan={4} style={{padding:"8px 4px",color:"var(--text-muted,#8892a4)"}}>…and {rows.length - 50} more in the CSV.</td></tr>}
              </tbody>
            </table>
          )}
        </div>

        <div className="pb-modal-actions">
          <button className="pb-btn" onClick={onClose}>Cancel</button>
          <button className="pb-btn pb-btn-accent" onClick={handleDownload} disabled={rows.length === 0}>Download CSV</button>
        </div>
      </div>
    </div>
  );
}

// Format a Date as YYYY-MM-DD in local time (toISOString would shift the day
// near midnight because it converts to UTC first).
function toDateInputValue(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function SupplierComparisonModal({ allSuppliers, onDownload, onClose }) {
  // Default period = the previous full calendar month.
  const [from, setFrom] = useState(() => {
    const now = new Date();
    return toDateInputValue(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  });
  const [to, setTo] = useState(() => {
    const now = new Date();
    return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 0));
  });
  const [supplier, setSupplier] = useState("ALL");
  const [minDiffPct, setMinDiffPct] = useState(10);
  const [busy, setBusy] = useState(false);
  const [runError, setRunError] = useState("");
  const [result, setResult] = useState(null);
  const [showSuspects, setShowSuspects] = useState(false);

  const runReport = async () => {
    setBusy(true);
    setRunError("");
    try {
      const res = await apiFetch("/api/price-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "purchaseComparison",
          from,
          to,
          supplier,
          minDiffPct: Number(minDiffPct),
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Comparison failed");
      setResult(data);
      setShowSuspects(false);
    } catch (err) {
      setRunError(err.message || "Comparison failed");
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const items = result?.items || [];
  const dataSuspects = result?.dataSuspects || [];
  const summary = result?.summary || {};
  const perSupplier = summary.perSupplier || {};
  const supplierNames = Object.keys(perSupplier).sort((a, b) => (Number(perSupplier[b]?.spend) || 0) - (Number(perSupplier[a]?.spend) || 0));
  const totalOverspend = Number(summary.totalOverspend);
  const unmatchedSpend = Number(summary.unmatchedSpend);

  const fmtPct = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? `${n.toFixed(1)}%` : "-";
  };

  const ourQty = (item) => {
    const bucket = item?.bySupplier?.[item?.ourSupplier];
    const n = Number(bucket?.qty);
    return Number.isFinite(n) ? n : null;
  };
  const ourWavg = (item) => item?.bySupplier?.[item?.ourSupplier]?.wavg;

  const handleDownload = () => {
    onDownload(
      `supplier-comparison-${from || "start"}-to-${to || "end"}.csv`,
      ["Item", "SKU", "Category", "Our supplier", "Qty", "Our avg", "Best other supplier", "Best other price", "Diff %", "$ impact", "Prior avg", "Drift %", "Flags"],
      items.map((item) => {
        const qty = ourQty(item);
        const wavg = Number(ourWavg(item));
        const bestPrice = Number(item?.bestOther?.wavg);
        const diffPct = Number(item?.diffPct);
        const overspend = Number(item?.overspendDollar);
        const priorWavg = Number(item?.selfHistory?.priorWavg);
        const driftPct = Number(item?.selfHistory?.driftPct);
        return [
          item?.description || "",
          item?.sku || "",
          item?.category || "",
          item?.ourSupplier || "",
          qty == null ? "" : qty,
          Number.isFinite(wavg) ? wavg.toFixed(2) : "",
          item?.bestOther?.supplier || "",
          Number.isFinite(bestPrice) ? bestPrice.toFixed(2) : "",
          Number.isFinite(diffPct) ? diffPct.toFixed(1) : "",
          Number.isFinite(overspend) ? overspend.toFixed(2) : "",
          Number.isFinite(priorWavg) ? priorWavg.toFixed(2) : "",
          Number.isFinite(driftPct) ? driftPct.toFixed(1) : "",
          (item?.flags || []).join("; "),
        ];
      })
    );
  };

  const renderRow = (item, i) => {
    const qty = ourQty(item);
    return (
      <tr key={i} style={{borderTop:"1px solid rgba(42,49,64,.55)"}}>
        <td style={{padding:"6px 4px"}}>
          {(item?.description || "").slice(0, 50)}
          <br/>
          <span style={{color:"var(--text-muted,#8892a4)",fontSize:10}}>{item?.sku || "-"}{item?.category ? ` - ${item.category}` : ""}</span>
        </td>
        <td style={{padding:"6px 4px"}}>{item?.ourSupplier || "-"}</td>
        <td style={{padding:"6px 4px",textAlign:"right"}}>{qty == null ? "-" : qty}</td>
        <td style={{padding:"6px 4px",textAlign:"right"}}>{fmtCurrency(ourWavg(item))}</td>
        <td style={{padding:"6px 4px"}}>
          {item?.bestOther?.supplier ? (
            <>
              {fmtCurrency(item.bestOther.wavg)}
              <br/>
              <span style={{color:"var(--text-muted,#8892a4)",fontSize:10}}>{item.bestOther.supplier}</span>
            </>
          ) : "-"}
        </td>
        <td style={{padding:"6px 4px",textAlign:"right",color:"#fca5a5"}}>{fmtPct(item?.diffPct)}</td>
        <td style={{padding:"6px 4px",textAlign:"right"}}>{fmtCurrency(item?.overspendDollar)}</td>
        <td style={{padding:"6px 4px",fontSize:10,color:"var(--text-muted,#8892a4)"}}>{(item?.flags || []).join(", ") || "-"}</td>
      </tr>
    );
  };

  return (
    <div className="pb-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="pb-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth:860,width:"95%"}}>
        <div className="pb-modal-header">
          <div>
            <div className="pb-modal-title">Supplier comparison</div>
            <div className="pb-modal-sub">What each supplier actually billed us over a period, compared item-by-item against other suppliers.</div>
          </div>
          <button type="button" className="pb-link-btn" onClick={onClose}>Close</button>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10}}>
          <label className="pb-modal-label">From
            <input type="date" className="pb-modal-input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="pb-modal-label">To
            <input type="date" className="pb-modal-input" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label className="pb-modal-label">Supplier
            <select className="pb-modal-input" value={supplier} onChange={(e) => setSupplier(e.target.value)}>
              <option value="ALL">ALL SUPPLIERS</option>
              {allSuppliers.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="pb-modal-label">Min diff %
            <select className="pb-modal-input" value={minDiffPct} onChange={(e) => setMinDiffPct(Number(e.target.value))}>
              <option value={5}>5%</option>
              <option value={10}>10%</option>
              <option value={15}>15%</option>
              <option value={20}>20%</option>
              <option value={30}>30%</option>
            </select>
          </label>
          <div style={{display:"flex",alignItems:"flex-end"}}>
            <button type="button" className="pb-btn pb-btn-accent" style={{width:"100%"}} onClick={runReport} disabled={busy}>
              {busy ? "Running…" : "Run report"}
            </button>
          </div>
        </div>

        {runError && <div className="pb-message error" style={{marginTop:8,marginBottom:0}}>{runError}</div>}

        {result && (
          <>
            <div className="pb-message" style={{marginTop:8,marginBottom:0}}>
              {supplierNames.length > 0 && (
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                  {supplierNames.map((name) => {
                    const s = perSupplier[name] || {};
                    return (
                      <span key={name} style={{background:"rgba(13,15,18,.4)",border:"1px solid rgba(42,49,64,.55)",borderRadius:999,padding:"5px 10px",fontSize:11}}>
                        <strong>{name}</strong> {fmtCurrency(s.spend)}
                        <span style={{color:"var(--text-muted,#8892a4)"}}> · {Number(s.lines) || 0} lines · {Number(s.invoices) || 0} inv</span>
                      </span>
                    );
                  })}
                </div>
              )}
              <strong>{items.length}</strong> flagged item{items.length === 1 ? "" : "s"}.
              {" "}Potential overspend: <strong>{Number.isFinite(totalOverspend) ? fmtCurrency(totalOverspend) : "-"}</strong>.
              {" "}Data suspects: <strong>{dataSuspects.length}</strong>.
              {" "}Unmatched spend: <strong>{Number.isFinite(unmatchedSpend) ? fmtCurrency(unmatchedSpend) : "-"}</strong>.
            </div>

            <div style={{maxHeight:"38vh",overflow:"auto",fontSize:12,marginTop:4}}>
              {items.length === 0 ? (
                <div className="pb-empty">No items met the {minDiffPct}% threshold for this period. Try widening the dates or lowering the minimum diff.</div>
              ) : (
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{textAlign:"left",color:"var(--text-muted,#8892a4)",fontSize:11}}>
                      <th style={{padding:"6px 4px"}}>Item</th>
                      <th style={{padding:"6px 4px"}}>Our supplier</th>
                      <th style={{padding:"6px 4px",textAlign:"right"}}>Qty</th>
                      <th style={{padding:"6px 4px",textAlign:"right"}}>Our avg</th>
                      <th style={{padding:"6px 4px"}}>Best other</th>
                      <th style={{padding:"6px 4px",textAlign:"right"}}>Diff %</th>
                      <th style={{padding:"6px 4px",textAlign:"right"}}>$ impact</th>
                      <th style={{padding:"6px 4px"}}>Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.slice(0, 50).map(renderRow)}
                    {items.length > 50 && <tr><td colSpan={8} style={{padding:"8px 4px",color:"var(--text-muted,#8892a4)"}}>…and {items.length - 50} more in the CSV.</td></tr>}
                  </tbody>
                </table>
              )}
            </div>

            {dataSuspects.length > 0 && (
              <div style={{marginTop:4}}>
                <button type="button" className="pb-link-btn" onClick={() => setShowSuspects((v) => !v)}>
                  {showSuspects ? "▴ Hide" : "▾ Show"} data suspects (pack/UOM) — {dataSuspects.length}
                </button>
                {showSuspects && (
                  <div style={{maxHeight:"24vh",overflow:"auto",fontSize:12,marginTop:4}}>
                    <div style={{fontSize:11,color:"var(--text-muted,#8892a4)",marginBottom:4}}>
                      Differences here are likely pack-size or unit-of-measure mismatches, not real price gaps. Verify before negotiating.
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <tbody>
                        {dataSuspects.map(renderRow)}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {!result && !runError && !busy && (
          <div className="pb-empty" style={{padding:"18px 12px"}}>Pick a period and hit Run report — it reads actual invoiced purchases, not just the price book.</div>
        )}

        <div className="pb-modal-actions">
          <button className="pb-btn" onClick={onClose}>Cancel</button>
          <button className="pb-btn pb-btn-accent" onClick={handleDownload} disabled={busy || items.length === 0}>Download CSV</button>
        </div>
      </div>
    </div>
  );
}

const PRICEBOOK_CSS = `
  .pb-wrap{max-width:1040px;margin:0 auto;padding:20px 16px 96px;color:var(--text,#e8eaf0)}
  .pb-header{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;margin-bottom:14px}
  .pb-title{font-size:30px;font-weight:700;font-family:'Bebas Neue',sans-serif;letter-spacing:.04em;color:var(--accent,#a5ce3a)}
  .pb-subtitle{font-size:13px;color:var(--text-muted,#8892a4)}
  .pb-actions{display:flex;gap:8px;flex-wrap:wrap}
  .pb-btn{background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:10px;color:var(--text,#e8eaf0);padding:10px 14px;cursor:pointer;font:inherit}
  .pb-btn-accent{background:var(--accent,#a5ce3a);border-color:var(--accent,#a5ce3a);color:#111;font-weight:700}
  .pb-stats{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-bottom:12px}
  .pb-stat{background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:12px;padding:14px}
  .pb-stat strong{display:block;font-size:18px;color:var(--text,#e8eaf0)}
  .pb-stat span{display:block;font-size:11px;color:var(--text-muted,#8892a4);text-transform:uppercase;letter-spacing:.05em;margin-top:4px}
  .pb-tabs{display:flex;gap:8px;margin-bottom:12px}
  .pb-tab{padding:8px 14px;border-radius:999px;border:1px solid var(--border,#2a3140);background:transparent;color:var(--text-muted,#8892a4);cursor:pointer}
  .pb-tab.active{border-color:var(--accent,#a5ce3a);background:rgba(165,206,58,.12);color:var(--accent,#a5ce3a)}
  .pb-message{background:rgba(59,130,246,.08);border:1px solid rgba(59,130,246,.22);border-radius:12px;padding:12px 14px;font-size:13px;line-height:1.5;margin-bottom:12px}
  .pb-message.error{background:rgba(239,68,68,.08);border-color:rgba(239,68,68,.25);color:#fca5a5}
  .pb-more{display:flex;gap:12px;align-items:center;justify-content:center;padding:14px 0 4px}
  .pb-more-btn{background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:999px;color:var(--text,#e8eaf0);padding:10px 20px;font:inherit;font-size:13px;cursor:pointer}
  .pb-more-btn:hover{border-color:var(--accent,#a5ce3a)}
  .pb-more-count{font-size:11px;color:var(--text-muted,#8892a4)}
  .pb-search{margin-bottom:12px}
  .pb-search input{width:100%;background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:12px;color:var(--text,#e8eaf0);padding:12px 14px;font:inherit}
  .pb-card{background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:14px;padding:14px;margin-bottom:12px}
  .pb-card-top{display:flex;justify-content:space-between;gap:14px;align-items:flex-start}
  .pb-card-title{font-size:15px;font-weight:700;display:flex;flex-wrap:wrap;gap:8px;align-items:center}
  .pb-overcharge-badge{display:inline-flex;align-items:center;gap:4px;background:rgba(239,68,68,.14);border:1px solid rgba(239,68,68,.45);color:#fca5a5;border-radius:999px;padding:3px 10px;font-size:11px;font-weight:600;letter-spacing:.02em}
  .pb-brand-badge{display:inline-flex;align-items:center;background:rgba(165,206,58,.12);border:1px solid rgba(165,206,58,.4);color:#a5ce3a;border-radius:999px;padding:2px 9px;font-size:11px;font-weight:700;letter-spacing:.02em;margin-left:8px;vertical-align:middle}
  .pb-brand-filter{display:inline-flex;gap:8px;align-items:center;background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:999px;padding:6px 14px;font-size:12px;color:var(--text,#e8eaf0)}
  .pb-brand-filter select{background:transparent;border:none;color:var(--text,#e8eaf0);font-size:12px;cursor:pointer;outline:none}
  .pb-filter-row{display:flex;gap:10px;align-items:center;margin-bottom:10px;flex-wrap:wrap}
  .pb-filter-toggle{display:inline-flex;gap:8px;align-items:center;background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:999px;padding:8px 14px;font-size:12px;color:var(--text,#e8eaf0);cursor:pointer}
  .pb-filter-toggle input{accent-color:var(--accent,#a5ce3a);cursor:pointer}
  .pb-filter-toggle em{font-style:normal;color:var(--text-muted,#8892a4);font-size:11px}
  .pb-cat-row{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px}
  .pb-cat-pill{background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:999px;padding:6px 12px;font-size:12px;color:var(--text-muted,#8892a4);cursor:pointer;font:inherit;display:inline-flex;gap:6px;align-items:center}
  .pb-cat-pill:hover{border-color:var(--accent,#a5ce3a);color:var(--text,#e8eaf0)}
  .pb-cat-pill.active{background:rgba(165,206,58,.12);border-color:var(--accent,#a5ce3a);color:var(--accent,#a5ce3a)}
  .pb-cat-pill em{font-style:normal;color:var(--text-muted,#8892a4);font-size:10px}
  .pb-cat-pill.active em{color:var(--accent,#a5ce3a)}
  .pb-date-filter{display:inline-flex;gap:8px;align-items:center;font-size:11px;color:var(--text-muted,#8892a4);flex-wrap:wrap}
  .pb-date-filter label{display:inline-flex;gap:4px;align-items:center}
  .pb-date-filter input{background:var(--surface2,#1e2328);border:1px solid var(--border,#2a3140);border-radius:6px;color:var(--text,#e8eaf0);padding:4px 8px;font:inherit;font-size:11px}
  .pb-admin-wrap{position:relative}
  .pb-admin-menu{position:absolute;top:42px;right:0;z-index:5;min-width:240px;background:var(--surface2,#1e2328);border:1px solid var(--border,#2a3140);border-radius:12px;padding:8px;box-shadow:0 12px 24px rgba(0,0,0,.35);display:flex;flex-direction:column;gap:4px}
  .pb-admin-menu button{background:none;border:none;text-align:left;color:var(--text,#e8eaf0);padding:8px 10px;border-radius:8px;cursor:pointer;font:inherit;font-size:13px}
  .pb-admin-menu button:hover:not(:disabled){background:rgba(165,206,58,.12);color:var(--accent,#a5ce3a)}
  .pb-admin-menu button:disabled{opacity:.55;cursor:wait}
  .pb-admin-section{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:var(--text-muted,#8892a4);padding:6px 10px 2px;margin-top:4px;border-top:1px solid var(--border,#2a3140)}
  .pb-admin-section:first-child{border-top:none;margin-top:0}
  .pb-card-sub{font-size:12px;color:var(--text-muted,#8892a4);margin-top:4px}
  .pb-part-line{margin-top:6px}
  .pb-part-badge{display:inline-block;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;font-weight:700;letter-spacing:.03em;background:rgba(120,170,255,.12);border:1px solid rgba(120,170,255,.4);color:#8fb4ff;border-radius:6px;padding:3px 8px}
  .pb-trade-names{display:flex;gap:8px;flex-wrap:wrap;margin-top:8px}
  .pb-trade-names span{background:rgba(165,206,58,.12);border:1px solid rgba(165,206,58,.24);border-radius:999px;padding:6px 10px;font-size:11px;color:var(--accent,#a5ce3a)}
  .pb-price-strip{display:grid;grid-template-columns:repeat(3,minmax(88px,1fr));gap:10px;min-width:300px}
  .pb-price-strip div{background:rgba(13,15,18,.4);border:1px solid rgba(42,49,64,.55);border-radius:10px;padding:10px}
  .pb-price-strip span{display:block;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:var(--text-muted,#8892a4)}
  .pb-price-strip strong{display:block;font-size:14px;margin-top:4px}
  .pb-price-strip em{display:block;font-style:normal;font-size:11px;color:var(--text-muted,#8892a4);margin-top:4px}
  .pb-card-controls{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:10px}
  .pb-link-btn{background:none;border:none;color:var(--accent,#a5ce3a);font:inherit;cursor:pointer;padding:0}
  .pb-recat{position:relative}
  .pb-recat-menu{position:absolute;top:24px;right:0;z-index:5;min-width:240px;background:var(--surface2,#1e2328);border:1px solid var(--border,#2a3140);border-radius:12px;padding:8px;box-shadow:0 12px 24px rgba(0,0,0,.35);display:flex;flex-direction:column;gap:4px}
  .pb-recat-menu button{background:none;border:none;text-align:left;color:var(--text,#e8eaf0);padding:8px 10px;border-radius:8px;cursor:pointer;font:inherit}
  .pb-recat-menu button:hover{background:rgba(165,206,58,.12);color:var(--accent,#a5ce3a)}
  .pb-recat-section{border-top:1px solid var(--border,#2a3140);margin-top:6px;padding-top:6px}
  .pb-recat-label{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:var(--text-muted,#8892a4);padding:0 10px 4px}
  .pb-recat-menu .pb-recat-danger{color:#ff7373}
  .pb-recat-menu .pb-recat-danger:hover{background:rgba(255,80,80,.12);color:#ff8a8a}
  .pb-modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:50;padding:20px}
  .pb-modal{background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:14px;padding:18px;max-width:560px;width:100%;max-height:80vh;display:flex;flex-direction:column;gap:12px}
  .pb-modal-header{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
  .pb-modal-title{font-size:16px;font-weight:700}
  .pb-modal-sub{font-size:12px;color:var(--text-muted,#8892a4);margin-top:4px}
  .pb-modal-search,.pb-modal-input{width:100%;background:var(--surface2,#1e2328);border:1px solid var(--border,#2a3140);border-radius:10px;color:var(--text,#e8eaf0);padding:10px 12px;font:inherit;box-sizing:border-box}
  .pb-modal-results{overflow:auto;display:flex;flex-direction:column;gap:6px;max-height:50vh}
  .pb-modal-result{display:flex;justify-content:space-between;align-items:center;gap:12px;background:rgba(13,15,18,.4);border:1px solid rgba(42,49,64,.55);border-radius:10px;padding:10px 12px;cursor:pointer;color:var(--text,#e8eaf0);font:inherit;text-align:left}
  .pb-modal-result:hover{border-color:var(--accent,#a5ce3a)}
  .pb-modal-result-title{font-size:13px;font-weight:600}
  .pb-modal-result-sub{font-size:11px;color:var(--text-muted,#8892a4);margin-top:4px}
  .pb-modal-pick{font-size:11px;font-weight:700;color:var(--accent,#a5ce3a);text-transform:uppercase;letter-spacing:.05em}
  .pb-modal-label{display:flex;flex-direction:column;gap:6px;font-size:12px;color:var(--text-muted,#8892a4)}
  .pb-modal-hint{font-size:11px;line-height:1.45;color:var(--text-muted,#8892a4);opacity:.8}
  .pb-modal-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:4px}
  .pb-suppliers{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}
  .pb-supplier-table-wrap{margin-top:12px;overflow:auto}
  .pb-supplier-table{display:grid;grid-auto-flow:column;grid-auto-columns:minmax(160px,1fr);gap:10px}
  .pb-supplier-col{background:rgba(13,15,18,.4);border:1px solid rgba(42,49,64,.55);border-radius:12px;padding:12px}
  .pb-supplier-col-head{font-size:12px;font-weight:700;color:var(--text,#e8eaf0)}
  .pb-supplier-col-price{font-size:15px;font-weight:700;margin-top:8px}
  .pb-supplier-col-meta{font-size:11px;color:var(--text-muted,#8892a4);margin-top:6px}
  /* Monospace so codes line up down the column and a transposed character is
     visible at a glance — this is the string that gets read out at a counter. */
  .pb-supplier-col-code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;font-weight:700;color:#8fb4ff;margin-top:6px;word-break:break-word}
  .pb-supplier-col-nocode{font-style:italic;opacity:.65}
  .pb-review-row{display:flex;gap:8px;flex-wrap:wrap;margin-top:12px}
  .pb-review-meta{display:flex;gap:8px;flex-wrap:wrap;margin-top:8px}
  .pb-review-meta span{background:rgba(13,15,18,.4);border:1px solid rgba(42,49,64,.55);border-radius:999px;padding:6px 10px;font-size:11px;color:var(--text-muted,#8892a4)}
  .pb-manual-input{background:var(--surface2,#1e2328);border:1px solid var(--border,#2a3140);border-radius:10px;color:var(--text,#e8eaf0);padding:10px 12px;font:inherit;min-width:120px}
  .pb-supplier-chip{background:rgba(13,15,18,.4);border:1px solid rgba(42,49,64,.55);border-radius:999px;padding:8px 12px;display:flex;gap:10px;align-items:center;font-size:12px}
  .pb-supplier-chip strong{font-size:12px}
  .pb-supplier-chip em{font-style:normal;color:var(--text-muted,#8892a4)}
  .pb-supplier-chip-btn{color:var(--text,#e8eaf0);font:inherit;cursor:pointer;transition:border-color .15s,background .15s}
  .pb-supplier-chip-btn:hover:not(:disabled){border-color:var(--accent,#a5ce3a);background:rgba(165,206,58,.08)}
  .pb-supplier-chip-btn:disabled{opacity:.55;cursor:wait}
  .pb-supplier-col-btn{color:var(--text,#e8eaf0);font:inherit;cursor:pointer;text-align:left;transition:border-color .15s,background .15s}
  .pb-supplier-col-btn:hover:not(:disabled){border-color:var(--accent,#a5ce3a);background:rgba(165,206,58,.08)}
  .pb-supplier-col-btn:disabled{opacity:.55;cursor:wait}
  .pb-inv-link{color:var(--accent,#a5ce3a);text-decoration:none}
  .pb-inv-link:hover{text-decoration:underline}
  .pb-empty,.pb-empty-inline{padding:28px 12px;text-align:center;color:var(--text-muted,#8892a4)}
  .pb-empty-inline{padding:8px 0;text-align:left}
  @media(min-width:768px){.pb-stats{grid-template-columns:repeat(4,minmax(0,1fr))}}
  @media(max-width:760px){.pb-card-top{flex-direction:column}.pb-price-strip{grid-template-columns:repeat(3,minmax(0,1fr));min-width:0;width:100%}}
`;
